import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { supabase } from './supabase';
import type {
  AchievementRow,
  AppointmentRow,
  BookingWaitlistRow,
  ClientRow,
  ClientChurnStatsView,
  ClientStatsView,
  ClientTierView,
  NoShowRow,
  RegularConversionStatsView,
  CustomTemplateRow,
  FeedbackKind,
  LeaderboardRow,
  LeaderboardScope,
  LessonRow,
  PhotoRow,
  RecapRow,
  ServiceRow,
  StreakRow,
  StreakShieldRow,
  UserLessonStateRow,
  UserRow,
  AcquisitionSource,
  CutQuality,
  PaymentMethod,
  VisitPhaseTime,
  VisitRow,
} from './types';

/* ============================================================================
 * Cache invalidation pub/sub
 *
 * Each useFetch consumer subscribes to one or more cache keys ('clients',
 * 'visits', 'appointments', etc.). Mutations call invalidate(key) to bump the
 * version, which forces every subscribed query to refetch. This replaces the
 * previous window.location.reload() hack.
 * ============================================================================ */

type CacheKey =
  | 'clients'
  | 'visits'
  | 'appointments'
  | 'photos'
  | 'profile'
  | 'stats'
  | 'streak'
  | 'lessons'
  | 'lessonState'
  | 'templates'
  | 'waitlist'
  | 'achievements'
  | 'services'
  | 'recaps'
  | 'capacity'
  | 'noShows';

const versions = new Map<CacheKey, number>();
const listeners = new Map<CacheKey, Set<() => void>>();

export function invalidate(...keys: CacheKey[]) {
  for (const key of keys) {
    versions.set(key, (versions.get(key) ?? 0) + 1);
    listeners.get(key)?.forEach((fn) => fn());
  }
}

function subscribe(keys: readonly CacheKey[], cb: () => void): () => void {
  for (const key of keys) {
    let set = listeners.get(key);
    if (!set) {
      set = new Set();
      listeners.set(key, set);
    }
    set.add(cb);
  }
  return () => {
    for (const key of keys) {
      listeners.get(key)?.delete(cb);
    }
  };
}

function getSnapshot(keys: readonly CacheKey[]): number {
  let sum = 0;
  for (const key of keys) sum += versions.get(key) ?? 0;
  return sum;
}

function useCacheVersion(keys: readonly CacheKey[]): number {
  // Stable subscription using stringified key list — caller passes a literal array
  // so reference equality is fine across renders for our use case, but we use a ref
  // to be safe against rerenders that pass new array literals.
  const keysRef = useRef(keys);
  if (keysRef.current.join('|') !== keys.join('|')) keysRef.current = keys;
  return useSyncExternalStore(
    (cb) => subscribe(keysRef.current, cb),
    () => getSnapshot(keysRef.current),
    () => 0
  );
}

/* ============================================================================
 * useFetch — generic single-fetch hook with cache-key reactivity
 * ============================================================================ */

type AsyncState<T> = { data: T | null; loading: boolean; error: string | null };

function useFetch<T>(
  fetcher: () => PromiseLike<{ data: T | null; error: { message: string } | null }>,
  deps: unknown[],
  cacheKeys: readonly CacheKey[] = []
) {
  const version = useCacheVersion(cacheKeys);
  const [state, setState] = useState<AsyncState<T>>({ data: null, loading: true, error: null });
  useEffect(() => {
    let alive = true;
    setState((prev) => ({ data: prev.data, loading: true, error: null }));
    Promise.resolve(fetcher()).then(({ data, error }) => {
      if (!alive) return;
      setState({ data: data ?? null, loading: false, error: error?.message ?? null });
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, version]);
  return state;
}

/* ============================================================================
 * Read hooks
 * ============================================================================ */

export function useCurrentUserProfile() {
  return useFetch<UserRow>(
    () => supabase.from('users').select('*').single(),
    [],
    ['profile']
  );
}

// Streak: a single row per user. The row may not exist yet (no auto-create-on-signup
// trigger for streaks — created server-side when the streak-bump function lands in v1.1).
// Use maybeSingle() so a missing row returns null cleanly instead of an error.
export function useCurrentStreak() {
  return useFetch<StreakRow | null>(
    () => supabase.from('streaks').select('*').maybeSingle(),
    [],
    ['streak']
  );
}

/** Active (unused, unexpired) streak shields for the current user.
 *  Returns rows in oldest-first order so the UI can show "next to expire."
 *  The rescue RPC consumes oldest-first too. */
export function useStreakShields() {
  return useFetch<StreakShieldRow[]>(
    () =>
      supabase
        .from('streak_shields')
        .select('*')
        .is('used_at', null)
        .gt('expires_at', new Date().toISOString())
        .order('granted_at', { ascending: true }),
    [],
    ['streak'],
  );
}

/** Spend p_shields shields and restore the pre-break streak + today's
 *  engagement. Caller (rescue modal) computes p_shields from the broken
 *  streak's day-gap (1 shield per 7 missed days, partial = full). The
 *  server double-checks shield availability and refuses if insufficient. */
export async function rescueStreak(p_shields: number) {
  const { data, error } = await supabase.rpc('rescue_streak', { p_shields });
  if (!error) invalidate('streak');
  return { data: (data ?? [])[0] ?? null, error };
}

/** "No, let it die." Clears pre_break_streak + broke_at so the rescue
 *  modal doesn't reappear. Shields are NOT consumed. */
export async function declineRescue() {
  const { error } = await supabase.rpc('decline_rescue');
  if (!error) invalidate('streak');
  return { error };
}

/** Lazy break detection — runs the cooloff math against today's date
 *  with NO engagement assumed, so a barber who hasn't logged a cut or
 *  completed a lesson still sees their streak break, cooloff bank
 *  drain, and rescue prompt fire on the next app open. The server
 *  function is idempotent (same-day re-calls are no-ops, broke_at-
 *  already-set is a no-op too) so calling it on every app start is
 *  safe and cheap.
 *
 *  Migration 0078 is the source of truth — the function walks the
 *  same earn/spend cadence as bump_streak_for_user but treats every
 *  day in the gap as missed. */
export async function peekStreakState() {
  const { error } = await supabase.rpc('peek_streak_state');
  if (!error) invalidate('streak');
  return { error };
}

/* ============================================================================
 * Lessons
 * ============================================================================ */

export function useAllLessons() {
  return useFetch<LessonRow[]>(
    () => supabase.from('lessons').select('*').order('category', { ascending: true }),
    [],
    ['lessons']
  );
}

// Today's lesson — deterministic per-day rotation over the published lesson library.
// Same lesson for everyone on a given calendar day (in v1.1 this becomes per-user via
// the `daily_insights` table populated by a server cron that weights by user goals).
//
// Returns the lesson + the user's reflection state (or null if not reflected yet).
export function useTodaysLesson() {
  return useFetch<{ lesson: LessonRow; state: UserLessonStateRow | null } | null>(
    async () => {
      // Pull the full library + the user's profile (for goals) + their
      // delivered set in parallel.
      const [lessonsRes, profileRes, statesRes] = await Promise.all([
        supabase.from('lessons').select('*').order('id', { ascending: true }),
        supabase.from('users').select('goals').single(),
        supabase.from('user_lesson_state').select('lesson_id, viewed_at'),
      ]);
      if (lessonsRes.error) return { data: null, error: lessonsRes.error };
      const lessons = lessonsRes.data ?? [];
      if (lessons.length === 0) return { data: null, error: null };

      // Apply the demo's pickTodaysLesson rules — calendar schedule beats goal
      // default beats TODAY_DEFAULT. Imported lazily to avoid a circular dep
      // and keep the @busy/core module out of the API package's build graph
      // for environments that don't need it.
      const { pickTodaysLessonId } = await import('@busy/core');
      const validIds = new Set(lessons.map((l) => l.id));
      const goals = (profileRes.data?.goals ?? []) as string[];

      // Build the rotation cooldown set: lessons delivered to this
      // barber within the last 90 days (and on a PRIOR day — today's
      // in-progress pick can re-serve on refresh). The picker uses
      // this as a hard-skip set, guaranteeing at least 90 days between
      // repeats of the same lesson.
      const localMidnight = new Date();
      localMidnight.setHours(0, 0, 0, 0);
      const cutoffIso = localMidnight.toISOString();
      const ninetyDaysAgoIso = new Date(
        Date.now() - 90 * 24 * 60 * 60 * 1000,
      ).toISOString();
      const recentDeliveredIds = new Set(
        (statesRes.data ?? [])
          .filter(
            (s) =>
              s.viewed_at &&
              s.viewed_at < cutoffIso &&
              s.viewed_at >= ninetyDaysAgoIso,
          )
          .map((s) => s.lesson_id),
      );

      // Catalog with category info — enables the picker's category-
      // preference fallback when the scheduled entry + goal anchor are
      // both on cooldown.
      const catalog = lessons.map((l) => ({ id: l.id, category: l.category }));

      const todayId = pickTodaysLessonId({
        goals,
        validIds,
        recentDeliveredIds,
        catalog,
      });
      // Fall back to the first row if the schedule yields an ID that somehow
      // isn't in the DB yet (only happens before the seed runs).
      const lesson = lessons.find((l) => l.id === todayId) ?? lessons[0];

      const { data: state } = await supabase
        .from('user_lesson_state')
        .select('*')
        .eq('lesson_id', lesson.id)
        .maybeSingle();

      // Stamp viewed_at on the first time today's lesson is fetched. Defines
      // the "delivered to your library" set — anything with viewed_at in
      // user_lesson_state is in the barber's permanent personal library,
      // even if they never voted, saved, or completed it. Idempotent: skip
      // if already stamped.
      if (!state?.viewed_at) {
        // Fire-and-forget so the hook isn't gated on the upsert.
        void markLessonDelivered(lesson.id);
      }

      return { data: { lesson, state: state ?? null }, error: null };
    },
    [],
    ['lessons', 'lessonState', 'profile']
  );
}

/**
 * Idempotently stamp viewed_at on a (user, lesson) row so the lesson lands
 * in the barber's "Delivered" library. If the row already exists with
 * viewed_at populated, this is a no-op.
 *
 * Used by useTodaysLesson when surfacing a daily pick, and by the bonus /
 * skip-ahead flows when handing the barber an early lesson.
 */
export async function markLessonDelivered(lessonId: string) {
  const userId = await getCurrentUserId();
  const { data: existing } = await supabase
    .from('user_lesson_state')
    .select('viewed_at, saved, completed, reflection, reflection_note')
    .eq('user_id', userId)
    .eq('lesson_id', lessonId)
    .maybeSingle();
  if (existing?.viewed_at) return { data: existing, error: null };
  const result = await supabase
    .from('user_lesson_state')
    .upsert(
      {
        user_id: userId,
        lesson_id: lessonId,
        saved: existing?.saved ?? false,
        completed: existing?.completed ?? false,
        reflection: existing?.reflection ?? null,
        reflection_note: existing?.reflection_note ?? null,
        viewed_at: new Date().toISOString(),
      },
      { onConflict: 'user_id,lesson_id' },
    )
    .select()
    .single();
  if (!result.error) invalidate('lessonState');
  return result;
}

/**
 * Pick a bonus lesson for the engagement reward after the barber marks
 * today's lesson complete. Picks an un-delivered lesson aligned to the
 * barber's primary goal (falls back to any un-delivered lesson if no
 * goal-matched ones remain). Stamps the row as delivered before returning.
 *
 * Returns null if every lesson has already been delivered (long-tenured
 * barber who's seen the whole catalog) — caller should hide the button.
 */
/** 24h cooldown between bonus-lesson claims — keeps the drip model intact
 *  (one daily lesson + at most one bonus = max two lessons read per day).
 *
 *  Enforcement: the gate read + cooldown-stamp write both happen client-side
 *  in this function (lines below). RLS scopes the write to the current
 *  user, so one user can't tamper with another's clock — but a tampered
 *  client of THEIR OWN account could in theory bypass the read-side gate.
 *  In practice this isn't an exploit anyone abuses (bonus lessons are
 *  small drip content, not progression rewards), but if we ever want a
 *  fully server-side gate it'd mirror `claim_skip_ahead` as a SECURITY
 *  DEFINER RPC. v1.1+ if abuse is observed. */
const BONUS_LESSON_COOLDOWN_MS = 24 * 60 * 60 * 1000;

export async function claimBonusLesson(): Promise<{ data: LessonRow | null; error: { message: string } | null }> {
  const userId = await getCurrentUserId();
  const [profileRes, lessonsRes, statesRes] = await Promise.all([
    supabase.from('users').select('goals, last_bonus_lesson_at').eq('id', userId).single(),
    supabase.from('lessons').select('*'),
    supabase.from('user_lesson_state').select('lesson_id, viewed_at').eq('user_id', userId),
  ]);
  if (lessonsRes.error) return { data: null, error: { message: lessonsRes.error.message } };

  // Cooldown gate — block the claim if the user picked a bonus inside the
  // last 24 hours. The error message includes a marker the UI can check
  // for to render a friendlier "comes back tomorrow" toast vs. a generic
  // server error.
  const lastBonus = profileRes.data?.last_bonus_lesson_at as string | null | undefined;
  if (lastBonus) {
    const elapsed = Date.now() - new Date(lastBonus).getTime();
    if (elapsed < BONUS_LESSON_COOLDOWN_MS) {
      return {
        data: null,
        error: { message: 'BONUS_COOLDOWN' },
      };
    }
  }

  const delivered = new Set(
    (statesRes.data ?? []).filter((s) => s.viewed_at).map((s) => s.lesson_id),
  );
  const undelivered = (lessonsRes.data ?? []).filter((l) => !delivered.has(l.id));
  if (undelivered.length === 0) return { data: null, error: null };

  // Prefer goal-aligned, fall through to anything un-delivered.
  const { GOAL_TO_CATEGORY } = await import('@busy/core');
  const goals = (profileRes.data?.goals ?? []) as string[];
  const targetCategory = goals[0] ? GOAL_TO_CATEGORY[goals[0]] : undefined;
  const aligned = targetCategory
    ? undelivered.filter((l) => l.category === targetCategory)
    : [];
  const pool = aligned.length > 0 ? aligned : undelivered;
  const pick = pool[Math.floor(Math.random() * pool.length)] ?? null;
  if (!pick) return { data: null, error: null };

  await markLessonDelivered(pick.id);
  // Stamp the cooldown — RLS scopes this update to the current user.
  await supabase.from('users').update({ last_bonus_lesson_at: new Date().toISOString() }).eq('id', userId);
  invalidate('profile');
  return { data: pick, error: null };
}

/**
 * Skip-ahead reward for streak ≥ 7. Validates the cooldown server-side
 * (1/week) via claim_skip_ahead RPC, then picks the lesson the schedule
 * would deliver tomorrow. Stamps it delivered before returning.
 */
export async function claimSkipAhead(): Promise<{ data: LessonRow | null; error: { message: string } | null }> {
  // Server-side cooldown gate. Throws if the barber claimed within 7 days.
  const claim = await supabase.rpc('claim_skip_ahead');
  if (claim.error) return { data: null, error: { message: claim.error.message } };
  invalidate('profile');

  const userId = await getCurrentUserId();
  const [profileRes, lessonsRes, statesRes] = await Promise.all([
    supabase.from('users').select('goals').eq('id', userId).single(),
    supabase.from('lessons').select('*'),
    supabase.from('user_lesson_state').select('lesson_id, viewed_at').eq('user_id', userId),
  ]);
  if (lessonsRes.error) return { data: null, error: { message: lessonsRes.error.message } };
  const lessons = lessonsRes.data ?? [];

  const { pickTodaysLessonId } = await import('@busy/core');
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const validIds = new Set(lessons.map((l) => l.id));
  const goals = (profileRes.data?.goals ?? []) as string[];
  // Skip-ahead pulls TOMORROW's lesson, so the cooldown set includes
  // every lesson delivered in the last 90 days INCLUDING today's pick
  // (without the < midnight filter that Today's lesson uses) — the
  // user explicitly tapped skip-ahead to get something genuinely new.
  const ninetyDaysAgoIso = new Date(
    Date.now() - 90 * 24 * 60 * 60 * 1000,
  ).toISOString();
  const recentDeliveredIds = new Set(
    (statesRes.data ?? [])
      .filter((s) => s.viewed_at && s.viewed_at >= ninetyDaysAgoIso)
      .map((s) => s.lesson_id),
  );
  const catalog = lessons.map((l) => ({ id: l.id, category: l.category }));
  const lessonId = pickTodaysLessonId({
    goals,
    validIds,
    recentDeliveredIds,
    catalog,
    now: tomorrow,
  });
  const pick = lessons.find((l) => l.id === lessonId) ?? null;
  if (!pick) return { data: null, error: null };

  await markLessonDelivered(pick.id);
  return { data: pick, error: null };
}

export async function setLessonReflection(
  lessonId: string,
  reflection: 'yes' | 'no'
) {
  // Voting "yes" on a previously-noted "no" lesson clears the stale note —
  // the original reason no longer applies. Voting "no" leaves any existing
  // note alone (the barber can still update it via the inline prompt).
  const patch: { reflection: 'yes' | 'no'; reflection_note?: null } =
    reflection === 'yes' ? { reflection, reflection_note: null } : { reflection };
  const result = await upsertLessonState(lessonId, patch);
  // Bump the helpful-stats cache so the poll readout updates everywhere
  // immediately — this barber's vote should be visible the moment they
  // submit it, no refresh needed.
  if (!('error' in result) || !result.error) invalidate('lessonState');
  return result;
}

/** Saves the barber's free-text reason for a "no" reflection vote. Used
 *  by the beta-feedback prompt that appears after the user votes "no" on
 *  a lesson. Trimmed empty notes are stored as null so we don't carry
 *  whitespace-only entries. */
export async function setLessonReflectionNote(
  lessonId: string,
  note: string,
) {
  const trimmed = note.trim();
  const result = await upsertLessonState(lessonId, {
    reflection_note: trimmed.length > 0 ? trimmed : null,
  });
  if (!('error' in result) || !result.error) invalidate('lessonState');
  return result;
}

export type LessonHelpfulStats = { yes_count: number; no_count: number; total: number };

/**
 * Live helpful-poll counts for one lesson. Aggregates votes across ALL barbers
 * — bypasses RLS via SECURITY DEFINER on the underlying RPC. Returns
 * { yes_count: 0, no_count: 0, total: 0 } before anyone has voted.
 */
export function useLessonHelpfulStats(lessonId: string | null | undefined) {
  return useFetch<LessonHelpfulStats>(
    async () => {
      if (!lessonId) return { data: null, error: null };
      const { data, error } = await supabase.rpc('lesson_helpful_stats', { p_lesson_id: lessonId });
      if (error) return { data: null, error: { message: error.message } };
      const row = (data ?? [])[0] ?? { yes_count: 0, no_count: 0, total: 0 };
      return { data: row as LessonHelpfulStats, error: null };
    },
    [lessonId ?? ''],
    ['lessonState'],
  );
}

export async function setLessonSaved(lessonId: string, saved: boolean) {
  return upsertLessonState(lessonId, { saved });
}

export async function setLessonCompleted(lessonId: string, completed: boolean) {
  return upsertLessonState(lessonId, { completed });
}

// Internal helper — upserts on (user_id, lesson_id) so reflection / saved / completed
// can be written independently without clobbering each other. Defaults preserve any
// existing flags via .upsert + the fields we explicitly set.
async function upsertLessonState(
  lessonId: string,
  patch: Partial<Pick<UserLessonStateRow, 'reflection' | 'reflection_note' | 'saved' | 'completed'>>
) {
  const userId = await getCurrentUserId();
  // Read the existing row (if any) so we don't blow away other fields when upserting.
  const { data: existing } = await supabase
    .from('user_lesson_state')
    .select('*')
    .eq('lesson_id', lessonId)
    .maybeSingle();
  const next: UserLessonStateRow = {
    user_id: userId,
    lesson_id: lessonId,
    saved: existing?.saved ?? false,
    completed: existing?.completed ?? false,
    reflection: existing?.reflection ?? null,
    reflection_note: existing?.reflection_note ?? null,
    viewed_at: new Date().toISOString(),
    ...patch,
  };
  const result = await supabase
    .from('user_lesson_state')
    .upsert(next, { onConflict: 'user_id,lesson_id' })
    .select()
    .single();
  if (!result.error) {
    // Lesson upserts fire two server-side triggers: bump_streak_for_user
    // (which can grant a cooloff or shield) and check_achievements
    // (which can land an achievement). Invalidate both caches so the
    // streak ribbon updates in real time AND the celebration queue
    // notices the new earn — fixes "had to leave and come back" lag.
    invalidate('lessonState', 'streak', 'achievements');
  }
  return result;
}

/* ============================================================================
 * Custom text templates
 *
 * Per the v1 design: 3 tones × 2 languages = 6 templates total per barber. Each
 * tone has a single editable template body. The custom_templates table still
 * has variant_idx in its primary key (legacy from the demo's rotation pool); we
 * always write variant_idx=0 and ignore the rest.
 *
 * Tone display labels ("Casual" / "Pro" / "Urgent") are also editable per
 * barber via profile.preferences.toneLabels. Default labels apply when unset.
 * ============================================================================ */

export type CustomTemplateMap = Record<string, Record<string, string>>;
// shape: map[tone][language] = body string

const TEMPLATE_VARIANT_IDX = 0; // always 0 — the schema column is legacy from the rotation-pool design

export function useCustomTemplates() {
  return useFetch<CustomTemplateMap>(
    async () => {
      const { data, error } = await supabase
        .from('custom_templates')
        .select('*')
        .eq('variant_idx', TEMPLATE_VARIANT_IDX);
      if (error) return { data: null, error };
      const map: CustomTemplateMap = {};
      (data ?? []).forEach((row) => {
        if (!map[row.tone]) map[row.tone] = {};
        map[row.tone][row.language] = row.body;
      });
      return { data: map, error: null };
    },
    [],
    ['templates']
  );
}

export async function setCustomTemplate(
  tone: 'casual' | 'pro' | 'urgent',
  language: 'en' | 'es',
  body: string
) {
  const userId = await getCurrentUserId();
  const result = await supabase
    .from('custom_templates')
    .upsert(
      {
        user_id: userId,
        tone,
        language,
        variant_idx: TEMPLATE_VARIANT_IDX,
        body,
      } as CustomTemplateRow,
      { onConflict: 'user_id,tone,language,variant_idx' }
    );
  if (!result.error) invalidate('templates');
  return result;
}

export async function resetCustomTemplate(
  tone: 'casual' | 'pro' | 'urgent',
  language: 'en' | 'es'
) {
  const result = await supabase
    .from('custom_templates')
    .delete()
    .eq('tone', tone)
    .eq('language', language)
    .eq('variant_idx', TEMPLATE_VARIANT_IDX);
  if (!result.error) invalidate('templates');
  return result;
}

export async function resetAllCustomTemplates() {
  // Delete-all without an explicit user filter — RLS already scopes deletes to the
  // current user, so this only wipes the calling barber's rows.
  const userId = await getCurrentUserId();
  const result = await supabase.from('custom_templates').delete().eq('user_id', userId);
  if (!result.error) invalidate('templates');
  return result;
}

// Per-barber tone labels — stored on profile.preferences.toneLabels JSONB. Falls
// back to defaults when unset. Apply globally regardless of language so a single
// rename ("Casual" → "Friendly check-in") shows everywhere it's used.
export type ToneLabels = Partial<Record<'casual' | 'pro' | 'urgent', string>>;

export async function setToneLabel(
  tone: 'casual' | 'pro' | 'urgent',
  label: string
): Promise<{ error: { message: string } | null }> {
  const userId = await getCurrentUserId();
  // Read existing preferences first so we don't blow away other keys
  const { data: existing, error: readErr } = await supabase
    .from('users')
    .select('preferences')
    .eq('id', userId)
    .single();
  if (readErr) return { error: { message: readErr.message } };
  const prefs = (existing?.preferences as Record<string, unknown> | null) ?? {};
  const toneLabels = (prefs.toneLabels as ToneLabels | undefined) ?? {};
  const trimmed = label.trim();
  if (trimmed) toneLabels[tone] = trimmed;
  else delete toneLabels[tone]; // empty string means reset to default
  const next = { ...prefs, toneLabels };
  const updateResult = await supabase
    .from('users')
    .update({ preferences: next })
    .eq('id', userId);
  if (!updateResult.error) invalidate('profile');
  return { error: updateResult.error ? { message: updateResult.error.message } : null };
}

// Read tone labels from the loaded profile row. Caller passes `profile.data`.
export function readToneLabels(profile: UserRow | null | undefined): ToneLabels {
  if (!profile) return {};
  const prefs = (profile.preferences as Record<string, unknown> | null) ?? {};
  return (prefs.toneLabels as ToneLabels | undefined) ?? {};
}

// Map of all the user's lesson states, keyed by lesson_id. Used by the archive
// so each row reflects its saved / completed / reflection status.
export function useAllLessonStates() {
  return useFetch<Record<string, UserLessonStateRow>>(
    async () => {
      const { data, error } = await supabase.from('user_lesson_state').select('*');
      if (error) return { data: null, error };
      const map: Record<string, UserLessonStateRow> = {};
      (data ?? []).forEach((row) => {
        map[row.lesson_id] = row;
      });
      return { data: map, error: null };
    },
    [],
    ['lessonState']
  );
}

export function useTodaysAppointments() {
  // "Today" in the user's local timezone. v1.1 will read STATE.timezone from the user profile.
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + 1);
  return useFetch<AppointmentRow[]>(
    () =>
      supabase
        .from('appointments')
        .select('*')
        .gte('start_at', start.toISOString())
        .lt('start_at', end.toISOString())
        .order('start_at', { ascending: true }),
    [start.toDateString()],
    ['appointments']
  );
}

export function useClients() {
  return useFetch<ClientRow[]>(
    () =>
      supabase
        .from('clients')
        .select('*')
        .is('archived_at', null)
        .order('created_at', { ascending: false }),
    [],
    ['clients']
  );
}

export function useArchivedClients() {
  return useFetch<ClientRow[]>(
    () =>
      supabase
        .from('clients')
        .select('*')
        .not('archived_at', 'is', null)
        .order('archived_at', { ascending: false }),
    [],
    ['clients']
  );
}

export function useClientStats() {
  return useFetch<ClientStatsView[]>(
    () => supabase.from('client_stats').select('*'),
    [],
    ['stats', 'visits']
  );
}

export function useClientTiers() {
  return useFetch<ClientTierView[]>(
    () => supabase.from('client_tier').select('*'),
    [],
    ['stats', 'visits']
  );
}

/** New-to-regular conversion rate (per-user, single row). View defined
 *  in migration 0073. Cached under the 'visits' key so the result
 *  invalidates whenever a visit is created/edited/deleted — keeps
 *  cohort sizes + conversion counts truthful without a manual refresh. */
export function useRegularConversion() {
  return useFetch<RegularConversionStatsView | null>(
    async () => {
      const res = await supabase
        .from('regular_conversion_stats')
        .select('*')
        .maybeSingle();
      // maybeSingle returns null for "no row" — that's the expected
      // shape for a brand-new account with no matured-cohort clients.
      // Don't treat it as an error.
      return { data: res.data ?? null, error: res.error };
    },
    [],
    ['stats', 'visits']
  );
}

/** Regular-client churn rate (per-user, single row). View defined in
 *  migration 0074. Pairs with useRegularConversion for the full
 *  retention story — conversion shows acquisition, churn shows what
 *  fraction of earned regulars have stopped coming. Returns null for
 *  accounts that have no regulars yet (cohort doesn't exist). */
export function useClientChurn() {
  return useFetch<ClientChurnStatsView | null>(
    async () => {
      const res = await supabase
        .from('client_churn_stats')
        .select('*')
        .maybeSingle();
      return { data: res.data ?? null, error: res.error };
    },
    [],
    ['stats', 'visits']
  );
}

// ────────── No-shows (migration 0076) ──────────

/** Log a no-show. Single mutation — minimal payload by design (the v1
 *  manual capture path is just "Mark no-show → pick client"). v1.1
 *  booking-platform integration will add scheduled_at population from
 *  the calendar event, but the row shape stays the same. */
export async function createNoShow(data: {
  client_id: string | null;
  /** Optional barber note. v1 doesn't surface this in UI yet; reserved
   *  for the flight-risk coaching surface coming with insights. */
  notes?: string;
  /** Optional override — v1 defaults to now() since capture is
   *  retroactive ("the slot just lapsed"). */
  scheduled_at?: string;
}): Promise<{ data: NoShowRow | null; error: { message: string } | null }> {
  const userId = await getCurrentUserId();
  const result = await supabase
    .from('no_shows')
    .insert({
      user_id: userId,
      client_id: data.client_id,
      scheduled_at: data.scheduled_at ?? new Date().toISOString(),
      notes: data.notes ?? null,
    })
    .select()
    .single();
  if (!result.error) invalidate('noShows', 'stats');
  return result as { data: NoShowRow | null; error: { message: string } | null };
}

/** All no-shows for the current user, newest-first. Powers the client-
 *  detail history line + the future no-show-rate dashboard surface. */
export function useNoShows() {
  return useFetch<NoShowRow[]>(
    () =>
      supabase
        .from('no_shows')
        .select('*')
        .order('scheduled_at', { ascending: false }),
    [],
    ['noShows'],
  );
}

/** No-shows for one client — fed into the client detail page so each
 *  client's no-show count + recent dates show in their history. */
export function useClientNoShows(clientId: string | null | undefined) {
  return useFetch<NoShowRow[]>(
    () =>
      supabase
        .from('no_shows')
        .select('*')
        .eq('client_id', clientId ?? '')
        .order('scheduled_at', { ascending: false }),
    [clientId ?? ''],
    ['noShows'],
  );
}

/**
 * Recent visit history for the current user — feeds the Cut Timer's smart
 * insights (computeCutInsights in @busy/core). Default 90-day window matches
 * the recap range cap; insight thresholds (>=3 same-type cuts, etc.) ignore
 * older history naturally.
 */
export function useRecentVisits(days: number = 90) {
  const start = new Date();
  start.setDate(start.getDate() - days);
  return useFetch<VisitRow[]>(
    () =>
      supabase
        .from('visits')
        .select('*')
        .gte('started_at', start.toISOString())
        .order('started_at', { ascending: false }),
    [days, start.toDateString()],
    ['visits'],
  );
}

/** Visits inside a dashboard RangeKey window, newest-first. Powers the
 *  Cut List route — same range logic as the Dashboard's metric grid so
 *  taps on a metric land on a list scoped to the user's expectation.
 *
 *  Custom ranges: pass `custom` with YYYY-MM-DD bounds. Without it, the
 *  query falls back to `range='today'` semantics (today only) — safer
 *  than throwing since the misconfigured caller still gets *some* rows
 *  rather than a crash. The Dashboard never hits this fallback. */
export function useVisitsInRange(range: RangeKey, custom?: CustomRangeBounds | null) {
  const start = rangeStartDate(range, custom);
  const end = rangeEndDate(range, custom);
  return useFetch<VisitRow[]>(
    () => {
      let q = supabase
        .from('visits')
        .select('*')
        .gte('started_at', start.toISOString());
      if (range === 'custom') {
        q = q.lte('started_at', end.toISOString());
      }
      return q.order('started_at', { ascending: false });
    },
    [range, start.toDateString(), end.toDateString()],
    ['visits'],
  );
}

export function useTodaysVisits() {
  // Today bound by the user's local timezone. v1.1 will use STATE.timezone from profile.
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + 1);
  return useFetch<VisitRow[]>(
    () =>
      supabase
        .from('visits')
        .select('*')
        .gte('started_at', start.toISOString())
        .lt('started_at', end.toISOString())
        .order('started_at', { ascending: false }),
    [start.toDateString()],
    ['visits']
  );
}

export function useClientVisits(clientId: string) {
  return useFetch<VisitRow[]>(
    () =>
      supabase
        .from('visits')
        .select('*')
        .eq('client_id', clientId)
        .order('started_at', { ascending: false }),
    [clientId],
    ['visits']
  );
}

export function useClientPhotos(clientId: string) {
  return useFetch<PhotoRow[]>(
    () =>
      supabase
        .from('photos')
        .select('*')
        .eq('client_id', clientId)
        .order('created_at', { ascending: false }),
    [clientId],
    ['photos']
  );
}

/* ============================================================================
 * Mutations
 * Every mutation calls invalidate(...) on the cache keys it touches.
 * ============================================================================ */

async function getCurrentUserId(): Promise<string> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');
  return user.id;
}

/** Strip all non-digit characters from a phone string. Used by the
 *  duplicate-phone guard so "(555) 123-4567" and "5551234567" match.
 *  The DB unique index on clients does the same regex normalization. */
export function normalizePhoneDigits(raw: string | null | undefined): string {
  if (!raw) return '';
  return raw.replace(/\D/g, '');
}

/** Find an active client whose phone matches the given digits. Used by
 *  the new-client form to surface a "you already have this person"
 *  warning before submission. Returns null when no match. Empty / null
 *  phone never matches. */
export async function findClientByPhone(rawPhone: string | null | undefined) {
  const digits = normalizePhoneDigits(rawPhone);
  if (!digits) return { data: null as ClientRow | null, error: null };
  const userId = await getCurrentUserId();
  // Server-side normalization via raw eq() over rpc would be cleaner but
  // adds an RPC; instead we widen with ilike on the original column,
  // then filter client-side. The ilike pattern matches any phone that
  // contains all the digits in order with anything between — covers
  // "(555) 123-4567" vs "5551234567" — and the client-side digit
  // comparison is the source of truth.
  const ilikePattern = `%${digits.split('').join('%')}%`;
  const result = await supabase
    .from('clients')
    .select('*')
    .eq('user_id', userId)
    .is('archived_at', null)
    .not('phone', 'is', null)
    .ilike('phone', ilikePattern)
    .limit(20);
  if (result.error) return { data: null as ClientRow | null, error: result.error };
  const match = (result.data ?? []).find(
    (c) => normalizePhoneDigits(c.phone) === digits,
  );
  return { data: match ?? null, error: null };
}

/**
 * Creates a client. If a `phone` is supplied, refuses (without
 * inserting) when an active client with the same digits already exists
 * — the DB also enforces this via the partial unique index in 0056.
 * The pre-flight check just turns the would-be 23505 into a friendly
 * error that includes the matching client's id so the UI can offer
 * "open existing" instead.
 */
export async function createClient(data: {
  first_name: string;
  last_name: string;
  phone?: string;
  notes?: string;
  /** Optional mm/dd birthday — both required together or both omitted.
   *  DB CHECK enforces the same invariant. Months 1-12, days 1-31. */
  birthday_month?: number | null;
  birthday_day?: number | null;
  /** Where this client came from (migration 0075). Captured once at
   *  creation, backfillable from client detail. Null = unspecified. */
  acquisition_source?: AcquisitionSource | null;
}) {
  const userId = await getCurrentUserId();
  const phone = data.phone?.trim() || null;

  if (phone) {
    const existing = await findClientByPhone(phone);
    if (existing.data) {
      return {
        data: null as ClientRow | null,
        error: {
          message: `A client with this phone already exists.`,
          code: 'duplicate_phone',
          existingClientId: existing.data.id,
        },
      };
    }
  }

  // Birthday must be both-or-neither (DB CHECK enforces too). Strip to
  // null/null if either is missing so the insert never half-populates.
  const bMonth =
    data.birthday_month != null && data.birthday_day != null
      ? data.birthday_month
      : null;
  const bDay =
    data.birthday_month != null && data.birthday_day != null
      ? data.birthday_day
      : null;

  const result = await supabase
    .from('clients')
    .insert({
      user_id: userId,
      first_name: data.first_name,
      last_name: data.last_name,
      phone,
      notes: data.notes ?? null,
      birthday_month: bMonth,
      birthday_day: bDay,
      source: 'custom',
      integration_id: null,
      integration_provider: null,
      integration_active: false,
      acquisition_source: data.acquisition_source ?? null,
    })
    .select()
    .single();
  if (!result.error) invalidate('clients', 'stats');
  return result;
}

export async function updateClient(id: string, data: Partial<ClientRow>) {
  // Phone-edit guard. If the caller is changing phone, ensure no other
  // active client owns the new digits. The DB index also enforces this;
  // the pre-flight check just gives a readable error.
  if (typeof data.phone === 'string' || data.phone === null) {
    const userId = await getCurrentUserId();
    const trimmed = (data.phone ?? '')?.toString().trim() || null;
    const digits = normalizePhoneDigits(trimmed);
    if (digits) {
      const ilikePattern = `%${digits.split('').join('%')}%`;
      const candidates = await supabase
        .from('clients')
        .select('id, phone')
        .eq('user_id', userId)
        .is('archived_at', null)
        .not('phone', 'is', null)
        .neq('id', id)
        .ilike('phone', ilikePattern)
        .limit(20);
      if (!candidates.error) {
        const conflict = (candidates.data ?? []).find(
          (c) => normalizePhoneDigits(c.phone) === digits,
        );
        if (conflict) {
          return {
            data: null as ClientRow | null,
            error: {
              message: 'Another client already uses this phone number.',
              code: 'duplicate_phone',
              existingClientId: conflict.id,
            },
          };
        }
      }
    }
  }

  // Birthday: both-or-neither at the DB CHECK level. If the caller touched
  // either column, copy through both (treating undefined as "no change"
  // would let a half-populated partial slip past the CHECK). The mm/dd
  // input always submits both together, so this is a defensive normalizer
  // for any future callers that might forget.
  const touchedBirthday =
    'birthday_month' in data || 'birthday_day' in data;
  if (touchedBirthday) {
    const m = data.birthday_month ?? null;
    const d = data.birthday_day ?? null;
    data.birthday_month = m != null && d != null ? m : null;
    data.birthday_day = m != null && d != null ? d : null;
  }

  const result = await supabase.from('clients').update(data).eq('id', id).select().single();
  if (!result.error) invalidate('clients');
  return result;
}

/**
 * Soft-delete a client. Sets archived_at = now() so:
 *   - useClients() (active list) hides the row.
 *   - client_stats / client_tier views exclude the row, so the client stops
 *     counting toward dashboard tier rollups + at-risk alerts.
 *   - Visits, appointments, photos all keep their FK link → per-client history
 *     is preserved and storage isn't orphaned.
 *   - useArchivedClients() lists archived rows so the user can restore.
 */
export async function archiveClient(id: string) {
  const result = await supabase
    .from('clients')
    .update({ archived_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();
  if (!result.error) invalidate('clients', 'stats');
  return result;
}

export async function restoreClient(id: string) {
  const result = await supabase
    .from('clients')
    .update({ archived_at: null })
    .eq('id', id)
    .select()
    .single();
  if (!result.error) invalidate('clients', 'stats');
  return result;
}

/**
 * Permanently deletes a client row. Visits keep their FK link
 * (`on delete set null`), so revenue / cuts / achievement counts are
 * preserved — the cuts just turn into anonymous walk-ins from the dashboard's
 * point of view. Photo blobs in the client-photos storage bucket are purged
 * first to avoid orphaning them when the photos rows cascade-delete.
 *
 * Only intended for archived clients (UX pre-condition); the data layer doesn't
 * enforce that since archived_at is just a column.
 */
export async function hardDeleteClient(id: string) {
  // Purge photo blobs from storage. Best-effort — if it fails we still proceed
  // (the photos rows cascade-delete via FK so orphans only affect the bucket).
  const photos = await supabase
    .from('photos')
    .select('storage_path')
    .eq('client_id', id);
  const paths = (photos.data ?? [])
    .map((p) => p.storage_path)
    .filter(Boolean) as string[];
  if (paths.length > 0) {
    for (let i = 0; i < paths.length; i += 500) {
      await supabase.storage.from('client-photos').remove(paths.slice(i, i + 500));
    }
  }

  const result = await supabase.from('clients').delete().eq('id', id);
  if (!result.error) invalidate('clients', 'photos', 'stats');
  return result;
}

/**
 * Upload a client photo: drops the file into the `client-photos` Storage bucket
 * at `{user_id}/{client_id}/{photo_id}.{ext}`, then inserts the matching row in
 * the `photos` table. Bucket is private — display goes through useClientPhotoUrls.
 *
 * Mirrors the demo's onClientPhotoSelected pattern (DEMO_REFERENCE.html:10589).
 */
export async function createClientPhoto(
  clientId: string,
  file: File,
  variant: 'before' | 'after' | 'reference' = 'reference',
) {
  const userId = await getCurrentUserId();
  const photoId = crypto.randomUUID();
  const ext = (file.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg';
  const storagePath = `${userId}/${clientId}/${photoId}.${ext}`;

  const upload = await supabase.storage
    .from('client-photos')
    .upload(storagePath, file, {
      contentType: file.type || 'image/jpeg',
      upsert: false,
    });
  if (upload.error) return { data: null, error: upload.error };

  const insert = await supabase
    .from('photos')
    .insert({
      id: photoId,
      user_id: userId,
      client_id: clientId,
      visit_id: null,
      storage_path: storagePath,
      variant,
      width: null,
      height: null,
    })
    .select()
    .single();
  if (insert.error) {
    // Best-effort cleanup so the bucket doesn't accumulate orphans
    await supabase.storage.from('client-photos').remove([storagePath]);
    return { data: null, error: insert.error };
  }
  invalidate('photos');
  return insert;
}

/**
 * Append-only log row for "I have permission to share" affirmations. Called
 * the moment a barber ticks the consent checkbox and proceeds with the share
 * — purely audit/legal, never used to gate the share itself.
 *
 * Best-effort: callers don't await this on the share path. If the insert
 * fails we don't surface the error (the share still proceeds), but we log
 * to console so it's visible in dev.
 */
export async function logPhotoShareConsent(args: {
  clientId: string | null;
  clientName: string;
  photoUrl: string | null;
  /** 'gallery' for the client-detail share modal, 'after_cut_step' for the
   *  in-flow composite share. */
  shareTarget: 'gallery' | 'after_cut_step' | 'transformation_reveal';
}) {
  try {
    const userId = await getCurrentUserId();
    const userAgent =
      typeof navigator !== 'undefined' && navigator.userAgent ? navigator.userAgent.slice(0, 500) : null;
    const result = await supabase.from('photo_share_consent_logs').insert({
      user_id: userId,
      client_id: args.clientId,
      client_name: args.clientName,
      photo_url: args.photoUrl,
      share_target: args.shareTarget,
      user_agent: userAgent,
    });
    if (result.error) {
      console.warn('[consent-log] insert failed', result.error.message);
    }
    return result;
  } catch (err) {
    console.warn('[consent-log] unexpected error', err);
    return { data: null, error: err as Error };
  }
}

export async function deleteClientPhoto(photoId: string, storagePath: string) {
  const dbResult = await supabase.from('photos').delete().eq('id', photoId);
  if (dbResult.error) return dbResult;
  await supabase.storage.from('client-photos').remove([storagePath]);
  invalidate('photos');
  return dbResult;
}

/**
 * Fetch signed URLs for a batch of photos. Bucket is private so direct .src
 * won't work — we mint a signed URL per photo (1-hour TTL). Returns a map of
 * { [photoId]: signedUrl }. Photos that fail to sign are simply omitted; the
 * UI falls back to a placeholder tile.
 */
export function useClientPhotoUrls(photos: PhotoRow[]) {
  const [urls, setUrls] = useState<Record<string, string>>({});
  useEffect(() => {
    if (photos.length === 0) {
      setUrls({});
      return;
    }
    let alive = true;
    (async () => {
      const result = await supabase.storage
        .from('client-photos')
        .createSignedUrls(
          photos.map((p) => p.storage_path),
          60 * 60,
        );
      if (!alive || result.error || !result.data) return;
      const map: Record<string, string> = {};
      result.data.forEach((entry, i) => {
        if (entry.signedUrl) map[photos[i].id] = entry.signedUrl;
      });
      setUrls(map);
    })();
    return () => {
      alive = false;
    };
  }, [photos]);
  return urls;
}

/**
 * Most-recent photo per client, signed and ready to drop into <Avatar>. Returns
 * a map keyed by client_id. Used by the Clients route + EOD wrap-up + anywhere
 * else a barber sees their roster as a list — saves the "initials" fallback for
 * clients with no photos on file.
 *
 * One-shot batch query + one createSignedUrls call regardless of roster size,
 * so it stays cheap even at 500+ clients.
 */
export function useLatestClientPhotoUrls() {
  return useFetch<Record<string, string>>(
    async () => {
      const { data, error } = await supabase
        .from('photos')
        .select('client_id, storage_path, created_at')
        .order('created_at', { ascending: false });
      if (error) return { data: null, error };
      // Keep only the latest photo per client_id (we ordered desc above so the
      // first occurrence we see is the most recent).
      const latest: Record<string, string> = {};
      for (const row of data ?? []) {
        if (!latest[row.client_id]) latest[row.client_id] = row.storage_path;
      }
      const paths = Object.values(latest);
      if (paths.length === 0) return { data: {}, error: null };
      const signed = await supabase.storage
        .from('client-photos')
        .createSignedUrls(paths, 60 * 60);
      if (signed.error || !signed.data) {
        return { data: null, error: signed.error ? { message: signed.error.message } : null };
      }
      const sigByPath: Record<string, string> = {};
      signed.data.forEach((entry, i) => {
        if (entry.signedUrl && paths[i]) sigByPath[paths[i]] = entry.signedUrl;
      });
      const out: Record<string, string> = {};
      Object.entries(latest).forEach(([clientId, path]) => {
        const url = sigByPath[path];
        if (url) out[clientId] = url;
      });
      return { data: out, error: null };
    },
    [],
    ['photos', 'clients'],
  );
}

// Bulk insert — used by the CSV import flow. Single round-trip via Supabase's
// array-form .insert(). RLS scopes inserts to the current user automatically.
//
// Achievements: the per-row trigger on `clients` runs check_achievements()
// after each insert, so milestones (10/25/50/100/250/500/750/1000) auto-award
// during import. We also fire one explicit recheck after the batch as a
// belt-and-suspenders measure — if the trigger ever fails or is dropped, the
// barber still gets every milestone they've earned.
//
// Phone duplicates: pre-filters input against (a) other rows in the same
// batch and (b) the user's existing active clients. Rows that would
// collide on normalized phone digits get skipped instead of failing the
// whole batch — the migration's unique index would otherwise abort the
// entire INSERT. The result includes the skipped count so the caller
// can show "imported X, skipped Y duplicates."
export async function createClientsBulk(
  rows: Array<{
    first_name: string;
    last_name: string;
    phone?: string | null;
    notes?: string | null;
    /** Optional mm/dd — accepted together-or-not-at-all. The DB CHECK
     *  rejects half-populated rows; we also normalize here so a
     *  caller can pass just one without the whole insert failing. */
    birthday_month?: number | null;
    birthday_day?: number | null;
  }>
) {
  if (rows.length === 0) return { data: [], error: null, skippedDuplicates: 0 };
  const userId = await getCurrentUserId();

  // Existing active phone digits for this user — used to dedupe against
  // the live roster.
  const existing = await supabase
    .from('clients')
    .select('phone')
    .eq('user_id', userId)
    .is('archived_at', null)
    .not('phone', 'is', null);
  const existingDigits = new Set<string>();
  for (const row of existing.data ?? []) {
    const d = normalizePhoneDigits(row.phone);
    if (d) existingDigits.add(d);
  }

  // Pre-filter: drop empty-name + dedupe-by-phone (against existing roster
  // AND within this batch). Rows with no phone always pass — they can't
  // collide on the unique index.
  const seenDigitsThisBatch = new Set<string>();
  let skippedDuplicates = 0;
  const cleaned: Array<{
    user_id: string;
    first_name: string;
    last_name: string;
    phone: string | null;
    notes: string | null;
    birthday_month: number | null;
    birthday_day: number | null;
    source: 'custom';
    integration_id: null;
    integration_provider: null;
    integration_active: false;
  }> = [];
  for (const r of rows) {
    const phone = r.phone?.trim() || null;
    const digits = normalizePhoneDigits(phone);
    if (digits) {
      if (existingDigits.has(digits) || seenDigitsThisBatch.has(digits)) {
        skippedDuplicates += 1;
        continue;
      }
      seenDigitsThisBatch.add(digits);
    }
    // Birthday: both-or-neither (DB CHECK enforces). If a caller passes
    // just one column, drop both rather than letting the whole insert
    // 23514 out on one stray row.
    const bMonth =
      r.birthday_month != null && r.birthday_day != null ? r.birthday_month : null;
    const bDay =
      r.birthday_month != null && r.birthday_day != null ? r.birthday_day : null;
    cleaned.push({
      user_id: userId,
      first_name: r.first_name,
      last_name: r.last_name,
      phone,
      notes: r.notes ?? null,
      birthday_month: bMonth,
      birthday_day: bDay,
      source: 'custom',
      integration_id: null,
      integration_provider: null,
      integration_active: false,
    });
  }

  if (cleaned.length === 0) {
    return { data: [], error: null, skippedDuplicates };
  }

  const result = await supabase.from('clients').insert(cleaned).select();
  if (!result.error) {
    invalidate('clients', 'stats');
    await supabase.rpc('check_achievements', { p_user: userId });
    invalidate('achievements');
  }
  return { ...result, skippedDuplicates };
}

/**
 * Mark the user's onboarding as complete + persist the final-step prefs in
 * one go. Sets `users.onboarded_at = now()` so the onboarding gate stops
 * redirecting them.
 */
/* ============================================================================
 * Booking integration waitlist — votes for which platform to ship next.
 * Backed by 0008_booking_waitlist.sql + booking_waitlist_count(p_platform) RPC.
 * ============================================================================ */

export function useUserWaitlist() {
  return useFetch<BookingWaitlistRow[]>(
    () => supabase.from('booking_waitlist').select('*').order('created_at', { ascending: false }),
    [],
    ['waitlist'],
  );
}

/**
 * Returns true if the current user has already voted for `platform`. Used by
 * the WaitlistModal to flip into the "you're already on the list" state.
 */
export function useHasVotedForPlatform(platform: string) {
  const all = useUserWaitlist();
  const voted = (all.data ?? []).some((row) => row.platform === platform);
  return { voted, loading: all.loading };
}

/**
 * Returns the global vote count for a platform, fetched via the
 * booking_waitlist_count RPC. Used to show "X other barbers are waiting".
 */
export function useWaitlistCount(platform: string) {
  const [data, setData] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const version = useCacheVersion(['waitlist']);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    supabase
      .rpc('booking_waitlist_count', { p_platform: platform })
      .then(({ data: count, error }) => {
        if (!alive) return;
        if (error) setData(null);
        else setData((count as number) ?? 0);
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [platform, version]);
  return { data, loading };
}

export async function joinWaitlist(platform: string, email?: string, feature?: string) {
  const userId = await getCurrentUserId();
  const result = await supabase
    .from('booking_waitlist')
    .insert({
      user_id: userId,
      platform,
      email: email ?? null,
      feature: feature ?? null,
    })
    .select()
    .single();
  if (!result.error) invalidate('waitlist');
  return result;
}

export async function leaveWaitlist(platform: string) {
  const userId = await getCurrentUserId();
  const result = await supabase
    .from('booking_waitlist')
    .delete()
    .eq('user_id', userId)
    .eq('platform', platform);
  if (!result.error) invalidate('waitlist');
  return result;
}

/* ============================================================================
 * Custom services — per-user cut types + add-ons that augment the built-in
 * CUT_TYPES / CUT_ADDONS catalog. Settings → Cut Timer manages these.
 * ============================================================================ */

export function useCustomServices() {
  return useFetch<ServiceRow[]>(
    () =>
      supabase
        .from('services')
        .select('*')
        .eq('is_archived', false)
        .order('created_at', { ascending: true }),
    [],
    ['services'],
  );
}

/**
 * Creates a service or add-on. Refuses (without inserting) if the user
 * already has a non-archived row with the same case-insensitive name +
 * is_addon kind — services and add-ons are independent namespaces, so
 * "Beard Trim" can exist as both, but two services both named "Beard
 * Trim" cannot. The DB also enforces this via a partial unique index
 * (migration 0055); this client-side check just gives a friendlier
 * error than the raw 23505 unique-violation surface.
 */
export async function createService(
  data: Partial<ServiceRow> & Pick<ServiceRow, 'name'>,
) {
  const userId = await getCurrentUserId();
  const trimmedName = data.name.trim();
  const isAddon = data.is_addon ?? false;
  if (!trimmedName) {
    return {
      data: null as ServiceRow | null,
      error: { message: 'Service name is required.', code: 'name_required' },
    };
  }

  // Pre-flight duplicate check. The unique index below is the source of
  // truth; we just translate the would-be 23505 into a readable result.
  const dup = await supabase
    .from('services')
    .select('id')
    .eq('user_id', userId)
    .eq('is_addon', isAddon)
    .eq('is_archived', false)
    .ilike('name', trimmedName)
    .limit(1)
    .maybeSingle();
  if (dup.data) {
    return {
      data: null as ServiceRow | null,
      error: {
        message: `${isAddon ? 'An add-on' : 'A service'} named "${trimmedName}" already exists.`,
        code: 'duplicate',
      },
    };
  }

  const result = await supabase
    .from('services')
    .insert({
      user_id: userId,
      name: trimmedName,
      price_cents: data.price_cents ?? 0,
      duration_min: data.duration_min ?? 30,
      phases: data.phases ?? [],
      is_addon: isAddon,
      icon: data.icon ?? 'scissors',
    })
    .select()
    .single();
  if (!result.error) invalidate('services');
  return result;
}

export async function updateService(id: string, data: Partial<ServiceRow>) {
  // Rename-into-duplicate guard. If the caller is changing name or
  // is_addon, ensure the new (lower(name), is_addon) coordinate is
  // free for this user (excluding the row we're updating). The DB
  // unique index also enforces this — this just produces a readable
  // error before round-tripping.
  if (typeof data.name === 'string' || typeof data.is_addon === 'boolean') {
    const userId = await getCurrentUserId();
    const trimmedName = (data.name ?? '').toString().trim();
    if (typeof data.name === 'string' && !trimmedName) {
      return {
        data: null as ServiceRow | null,
        error: { message: 'Service name is required.', code: 'name_required' },
      };
    }
    // Only check when we have a candidate name to compare against; if the
    // caller didn't pass a name, skip and rely on the DB index.
    if (typeof data.name === 'string') {
      const isAddon =
        typeof data.is_addon === 'boolean' ? data.is_addon : undefined;
      let q = supabase
        .from('services')
        .select('id')
        .eq('user_id', userId)
        .eq('is_archived', false)
        .ilike('name', trimmedName)
        .neq('id', id);
      if (typeof isAddon === 'boolean') q = q.eq('is_addon', isAddon);
      const dup = await q.limit(1).maybeSingle();
      if (dup.data) {
        return {
          data: null as ServiceRow | null,
          error: {
            message: `${isAddon ? 'An add-on' : 'A service'} named "${trimmedName}" already exists.`,
            code: 'duplicate',
          },
        };
      }
    }
  }

  const payload =
    typeof data.name === 'string' ? { ...data, name: data.name.trim() } : data;
  const result = await supabase
    .from('services')
    .update(payload)
    .eq('id', id)
    .select()
    .single();
  if (!result.error) invalidate('services');
  return result;
}

/**
 * Soft-deletes via is_archived = true so any historical visits referencing the
 * service name continue to read sensibly. Use deleteServiceHard for a real
 * delete (only safe before any visits exist).
 */
export async function archiveService(id: string) {
  const result = await supabase
    .from('services')
    .update({ is_archived: true })
    .eq('id', id);
  if (!result.error) invalidate('services');
  return result;
}

/* ============================================================================
 * Achievements — milestone tracking (streak / clients / cuts / revenue / lessons).
 * Backed by 0009_achievements.sql.
 * ============================================================================ */

export function useAchievements() {
  return useFetch<AchievementRow[]>(
    () => supabase.from('achievements').select('*').order('earned_at', { ascending: false }),
    [],
    ['achievements'],
  );
}

/**
 * Live "current value" for every achievement kind, computed by the DB
 * with the EXACT same rules check_achievements() uses to award tiers.
 * Backed by 0064's get_user_achievement_progress RPC.
 *
 * Critical: the Today screen's Next Milestones tile must use these
 * numbers (not roster counts or threshold floors) so what the barber
 * sees as their progress matches what the DB unlocks. Mismatched
 * counts erode trust — "I have 50 clients, why does it say 12?"
 */
export type AchievementProgress = {
  streak: number;
  clients: number;
  cuts: number;
  /** In cents — divide by 100 for dollars. */
  revenue_cents: number;
  lessons: number;
  transformations: number;
  timekeeper_hours: number;
};

/* ============================================================================
 * Engagement event logging (migration 0069). Fire-and-forget helpers — every
 * function swallows errors and returns the failure shape so callers don't
 * need defensive try/catch wrappers. Analytics tables; nothing user-facing
 * depends on them, so a failed write is a silent warning, not a UX break.
 * ============================================================================ */

/** Log an outreach event when the barber taps a draft in TextDraftPicker.
 *  `sent` reflects whether the share sheet was actually invoked. */
export async function logOutreachEvent(args: {
  clientId: string;
  draftKind: string;
  sent?: boolean;
}): Promise<void> {
  try {
    const userId = await getCurrentUserId();
    await supabase.from('outreach_event_log').insert({
      user_id: userId,
      client_id: args.clientId,
      draft_kind: args.draftKind,
      sent: args.sent ?? true,
    });
  } catch {
    /* analytics — never throw to caller */
  }
}

/** Log when a cut-timer flow closes without a successful save.
 *  `phase` should be one of the stable keys ('chair' | 'checkin' |
 *  'picker' | 'live' | 'reveal' | 'summary' | 'auto_stale'). */
export async function logCutTimerAbandon(args: {
  phase: string;
  clientId?: string | null;
  cutTypeName?: string | null;
  startedAt?: string | null;
  timerStarted?: boolean;
}): Promise<void> {
  try {
    const userId = await getCurrentUserId();
    await supabase.from('cut_timer_abandons').insert({
      user_id: userId,
      phase: args.phase,
      client_id: args.clientId ?? null,
      cut_type_name: args.cutTypeName ?? null,
      started_at: args.startedAt ?? null,
      timer_started: args.timerStarted ?? false,
    });
  } catch {
    /* analytics — never throw to caller */
  }
}

/** Open a lesson view session. Returns the client-generated session id
 *  the caller uses to close it later via endLessonViewSession. */
export async function startLessonViewSession(args: {
  lessonId: string;
}): Promise<string> {
  // Client-generated session id so we don't need a round-trip to read
  // back the inserted row's id before we can update it on unmount.
  const sessionId =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  try {
    const userId = await getCurrentUserId();
    await supabase.from('lesson_view_sessions').insert({
      user_id: userId,
      lesson_id: args.lessonId,
      client_session_id: sessionId,
    });
  } catch {
    /* analytics — never throw */
  }
  return sessionId;
}

/** Close a lesson view session — records the end timestamp + max
 *  scroll depth observed during the session. Idempotent on the session
 *  id; calling twice is harmless. */
export async function endLessonViewSession(args: {
  clientSessionId: string;
  maxScrollPct: number;
  completedInSession?: boolean;
}): Promise<void> {
  try {
    const userId = await getCurrentUserId();
    await supabase
      .from('lesson_view_sessions')
      .update({
        ended_at: new Date().toISOString(),
        max_scroll_pct: Math.max(0, Math.min(100, Math.round(args.maxScrollPct))),
        completed_in_session: args.completedInSession ?? false,
      })
      .eq('user_id', userId)
      .eq('client_session_id', args.clientSessionId);
  } catch {
    /* analytics — never throw */
  }
}

/** Increment the daily counter for a route visit. Calls the
 *  record_feature_usage RPC which upserts the aggregate row. */
export async function recordFeatureUsage(routeKey: string): Promise<void> {
  try {
    await supabase.rpc('record_feature_usage', { p_route_key: routeKey });
  } catch {
    /* analytics — never throw */
  }
}

/** Log an onboarding step completion. PK on (user_id, step_key) so
 *  duplicate completions don't skew the funnel — the insert silently
 *  errors and the helper swallows it. */
export async function logOnboardingStep(stepKey: string): Promise<void> {
  try {
    const userId = await getCurrentUserId();
    await supabase
      .from('onboarding_step_events')
      .insert({ user_id: userId, step_key: stepKey });
  } catch {
    /* analytics — never throw; duplicate inserts are expected */
  }
}

/** Log when the achievement celebration modal mounts for a given
 *  achievement. PK unique on (user_id, achievement_id) — duplicate
 *  fires are silently ignored. */
export async function logAchievementCelebrationView(
  achievementId: string,
): Promise<void> {
  try {
    const userId = await getCurrentUserId();
    await supabase
      .from('achievement_celebration_views')
      .insert({ user_id: userId, achievement_id: achievementId });
  } catch {
    /* analytics — never throw; duplicates are expected */
  }
}

/**
 * Lazy backfill of the daily-snapshot table (migration 0068). Called on
 * Dashboard mount to keep the snapshot history complete for active
 * users. Cheap when there's nothing to fill (single SELECT pre-flight
 * inside the RPC); when there IS work, walks day-by-day from the last
 * snapshot up through yesterday.
 *
 * Today's snapshot is intentionally not generated here — today's row
 * is generated on tomorrow's first backfill, after the day has fully
 * closed and any late visit edits have settled.
 *
 * Fire-and-forget. The Dashboard doesn't render anything from this
 * table — it's purely an analytics foundation, so a failed RPC is a
 * silent warning, not a user-visible error.
 */
export async function backfillDailySnapshots(): Promise<{
  generated: number;
  error: string | null;
}> {
  try {
    const userId = await getCurrentUserId();
    const { data, error } = await supabase.rpc('backfill_daily_snapshots', {
      p_user: userId,
    });
    if (error) return { generated: 0, error: error.message };
    return { generated: Number(data ?? 0), error: null };
  } catch (err) {
    return {
      generated: 0,
      error: err instanceof Error ? err.message : String(err),
    };
  }
}

export function useAchievementProgress() {
  return useFetch<AchievementProgress>(
    async () => {
      const userId = await getCurrentUserId();
      const { data, error } = await supabase.rpc('get_user_achievement_progress', {
        p_user: userId,
      });
      if (error) return { data: null, error: { message: error.message } };
      // RPC returns a single row; supabase wraps it in an array.
      const row = (data ?? [])[0];
      return {
        data: {
          streak: row?.streak ?? 0,
          clients: row?.clients ?? 0,
          cuts: row?.cuts ?? 0,
          revenue_cents: Number(row?.revenue_cents ?? 0),
          lessons: row?.lessons ?? 0,
          transformations: row?.transformations ?? 0,
          timekeeper_hours: row?.timekeeper_hours ?? 0,
        },
        error: null,
      };
    },
    [],
    // Re-fetch when anything that could move a counter changes — visits
    // affect cuts/revenue/transformations/timekeeper, achievements is a
    // proxy for "just-completed cut" (the post-cut flow inserts both),
    // streaks for streak, lessons for lessons, clients for the visit
    // join's anonymous filter.
    ['visits', 'achievements', 'streak', 'lessonState', 'clients'],
  );
}

/**
 * Returns achievements that haven't been read (celebrated) yet. The Today
 * screen shows a celebration modal on mount when this is non-empty.
 */
export function useUnreadAchievements() {
  return useFetch<AchievementRow[]>(
    () =>
      supabase
        .from('achievements')
        .select('*')
        .is('read_at', null)
        .order('earned_at', { ascending: true }),
    [],
    ['achievements'],
  );
}

/**
 * Mark a list of achievement IDs as celebrated. Called when the
 * CelebrationModal closes so the same milestone doesn't fire again on next mount.
 */
export async function markAchievementsRead(ids: string[]) {
  if (ids.length === 0) return { error: null };
  const result = await supabase
    .from('achievements')
    .update({ read_at: new Date().toISOString() })
    .in('id', ids);
  if (!result.error) invalidate('achievements');
  return result;
}

/**
 * Top-N leaderboard scoped to the viewer's area / state / country, plus the
 * viewer's pinned row when they're outside the top N. Server-side enforces
 * masking and profile-completeness — see migration 0031 for the full
 * privacy + ranking rules.
 *
 * Re-runs when achievements or user-profile cache keys flip so newly-earned
 * milestones move the leaderboard without requiring a manual refresh.
 */
export function useLeaderboard(scope: LeaderboardScope, limit: number = 10) {
  return useFetch<LeaderboardRow[]>(
    async () => {
      const { data, error } = await supabase.rpc('leaderboard_top', {
        p_scope: scope,
        p_limit: limit,
      });
      return { data: (data as LeaderboardRow[] | null) ?? [], error };
    },
    [scope, limit],
    ['achievements', 'profile'],
  );
}

/**
 * Manually re-run the achievements check for the current user. Triggers fire
 * automatically on data changes, but this is useful for backfilling existing
 * accounts on first deploy after the migration lands.
 */
export async function recheckAchievements() {
  const userId = await getCurrentUserId();
  const result = await supabase.rpc('check_achievements', { p_user: userId });
  if (!result.error) invalidate('achievements');
  return result;
}

/* ============================================================================
 * Privacy & data — export + account deletion
 * ============================================================================ */

export type AccountExport = {
  exported_at: string;
  schema_version: 1;
  user: UserRow | null;
  clients: ClientRow[];
  visits: VisitRow[];
  appointments: AppointmentRow[];
  photos: PhotoRow[];
  services: ServiceRow[];
  custom_templates: CustomTemplateRow[];
  user_lesson_state: UserLessonStateRow[];
  streak: StreakRow | null;
  achievements: AchievementRow[];
  booking_waitlist: BookingWaitlistRow[];
};

/**
 * Pulls every user-owned row across every table and bundles into one JSON.
 * RLS already restricts each query to the current user's rows, so we don't
 * need to filter by user_id ourselves. Photos include storage paths but not
 * the binary data — the user can fetch each via signed URL if they want it.
 */
export async function exportUserData(): Promise<{ data: AccountExport | null; error: { message: string } | null }> {
  try {
    const [
      user,
      clients,
      visits,
      appointments,
      photos,
      services,
      templates,
      lessonState,
      streak,
      achievements,
      waitlist,
    ] = await Promise.all([
      supabase.from('users').select('*').single(),
      supabase.from('clients').select('*'),
      supabase.from('visits').select('*'),
      supabase.from('appointments').select('*'),
      supabase.from('photos').select('*'),
      supabase.from('services').select('*'),
      supabase.from('custom_templates').select('*'),
      supabase.from('user_lesson_state').select('*'),
      supabase.from('streaks').select('*').maybeSingle(),
      supabase.from('achievements').select('*'),
      supabase.from('booking_waitlist').select('*'),
    ]);

    const firstError =
      user.error ||
      clients.error ||
      visits.error ||
      appointments.error ||
      photos.error ||
      services.error ||
      templates.error ||
      lessonState.error ||
      streak.error ||
      achievements.error ||
      waitlist.error;
    if (firstError) return { data: null, error: { message: firstError.message } };

    return {
      data: {
        exported_at: new Date().toISOString(),
        schema_version: 1,
        user: user.data ?? null,
        clients: clients.data ?? [],
        visits: visits.data ?? [],
        appointments: appointments.data ?? [],
        photos: photos.data ?? [],
        services: services.data ?? [],
        custom_templates: templates.data ?? [],
        user_lesson_state: lessonState.data ?? [],
        streak: streak.data ?? null,
        achievements: achievements.data ?? [],
        booking_waitlist: waitlist.data ?? [],
      },
      error: null,
    };
  } catch (e) {
    return { data: null, error: { message: e instanceof Error ? e.message : String(e) } };
  }
}

/**
 * Permanently deletes the user's account.
 *
 * 1. Removes every photo from the `client-photos` storage bucket. (auth.users
 *    cascade only covers tables, not storage objects — orphans would otherwise
 *    sit forever.)
 * 2. Calls the `delete_user_account` RPC, which deletes auth.users → cascades
 *    through public.users and every per-user table.
 *
 * After success the caller should sign out + navigate to landing.
 */
export async function deleteUserAccount(): Promise<{ error: { message: string } | null }> {
  // Storage purge — best-effort. We page through all the user's photo rows so
  // no single response is huge for power users.
  const photos = await supabase.from('photos').select('storage_path');
  if (photos.error) return { error: { message: photos.error.message } };
  const paths = (photos.data ?? []).map((p) => p.storage_path).filter(Boolean) as string[];
  if (paths.length > 0) {
    // Supabase storage `.remove()` accepts up to 1000 paths per call.
    for (let i = 0; i < paths.length; i += 500) {
      await supabase.storage.from('client-photos').remove(paths.slice(i, i + 500));
    }
  }

  const { error } = await supabase.rpc('delete_user_account');
  if (error) return { error: { message: error.message } };
  return { error: null };
}

/* ============================================================================
 * Recaps — weekly + monthly snapshots persisted on first view + auto-emailed
 * by the notify_recap trigger (migration 0026). Generation is lazy: the
 * dashboard checks if "last week's"/"last month's" recap exists when it
 * mounts and computes one if not.
 * ============================================================================ */

/** Recap archive for the dashboard. Returns every recap row whose
 *  period intersects [start, end] — so the recap list reflects the
 *  same time window the rest of the dashboard does.
 *
 *  Overlap (not strict containment) is the right semantic: a weekly
 *  recap for "Apr 1-7" should still appear when the dashboard range
 *  is "30 days ending May 5" even though period_start (Apr 1) sits
 *  outside the window's start (Apr 5). Strict containment would hide
 *  context the user expects to see.
 *
 *  Both bounds are YYYY-MM-DD strings. Pass null to disable filtering
 *  on that side (callers that want "everything ever" can pass two
 *  nulls). */
export function useRecaps(opts: {
  start?: string | null;
  end?: string | null;
} = {}) {
  const start = opts.start ?? null;
  const end = opts.end ?? null;
  return useFetch<RecapRow[]>(
    () => {
      let q = supabase.from('recaps').select('*');
      if (start) {
        // Period must end on/after the range start (i.e. period
        // intersects the range on its right edge).
        q = q.gte('period_end', start);
      }
      if (end) {
        // Period must start on/before the range end (i.e. period
        // intersects the range on its left edge).
        q = q.lte('period_start', end);
      }
      return q.order('period_start', { ascending: false });
    },
    [start ?? '', end ?? ''],
    ['recaps'],
  );
}

export type RecapPeriodInput = {
  kind: 'weekly' | 'monthly' | 'quarterly' | 'annual';
  periodStart: string;
  periodEnd: string;
};

/**
 * Compute + persist a recap for the given period. Idempotent — if a row
 * already exists for (user_id, kind, period_start), returns it without
 * re-inserting (the unique constraint would fire otherwise). Caller is
 * responsible for the "should we generate?" gate (e.g. only run on Monday
 * for last week's weekly recap).
 */
/** Compute the RecapData payload for a given period — shared by
 *  generateRecap (INSERT-on-first-time) + recomputeRecap (UPDATE on
 *  existing row, no email re-trigger). Returns the computed payload
 *  or an error to surface to the caller. */
async function buildRecapData(period: RecapPeriodInput): Promise<
  | { data: ReturnType<typeof import('@busy/core').computeRecap>; error: null }
  | { data: null; error: { message: string } }
> {
  const { priorPeriodBounds, computeRecap } = await import('@busy/core');
  const prior = priorPeriodBounds(period.periodStart, period.periodEnd);
  const startIso = period.periodStart + 'T00:00:00';
  const endIso = period.periodEnd + 'T23:59:59';
  const priorStartIso = prior.start + 'T00:00:00';
  const priorEndIso = prior.end + 'T23:59:59';

  const [visitsRes, priorVisitsRes, clientsRes, milestonesRes, lessonsRes, streakRes] = await Promise.all([
    supabase
      .from('visits')
      .select('id, client_id, cut_type, price_cents, tip_cents, default_price_cents, payment_method, was_appointment, duration_min, started_at, addons')
      .gte('started_at', startIso)
      .lte('started_at', endIso),
    supabase
      .from('visits')
      .select('id, client_id, cut_type, price_cents, tip_cents, default_price_cents, payment_method, was_appointment, duration_min, started_at, addons')
      .gte('started_at', priorStartIso)
      .lte('started_at', priorEndIso),
    supabase.from('clients').select('id, first_name, last_name, created_at'),
    supabase
      .from('achievements')
      .select('kind, threshold, earned_at')
      .gte('earned_at', startIso)
      .lte('earned_at', endIso),
    supabase
      .from('user_lesson_state')
      .select('lesson_id, completed, reflection, viewed_at')
      .gte('viewed_at', startIso)
      .lte('viewed_at', endIso),
    supabase.from('streaks').select('current_streak, longest_streak').maybeSingle(),
  ]);

  const firstError =
    visitsRes.error || priorVisitsRes.error || clientsRes.error ||
    milestonesRes.error || lessonsRes.error || streakRes.error;
  if (firstError) return { data: null, error: { message: firstError.message } };

  const data = computeRecap({
    kind: period.kind,
    periodStart: period.periodStart,
    periodEnd: period.periodEnd,
    visits: (visitsRes.data ?? []).map((v) => ({
      id: v.id,
      client_id: v.client_id,
      cut_type: v.cut_type,
      price_cents: v.price_cents ?? 0,
      tip_cents: v.tip_cents ?? 0,
      default_price_cents: v.default_price_cents ?? null,
      payment_method: v.payment_method ?? null,
      was_appointment: v.was_appointment ?? null,
      duration_min: v.duration_min,
      started_at: v.started_at,
      addons: Array.isArray(v.addons) ? v.addons : [],
    })),
    priorVisits: (priorVisitsRes.data ?? []).map((v) => ({
      id: v.id,
      client_id: v.client_id,
      cut_type: v.cut_type,
      price_cents: v.price_cents ?? 0,
      tip_cents: v.tip_cents ?? 0,
      default_price_cents: v.default_price_cents ?? null,
      payment_method: v.payment_method ?? null,
      was_appointment: v.was_appointment ?? null,
      duration_min: v.duration_min,
      started_at: v.started_at,
      addons: Array.isArray(v.addons) ? v.addons : [],
    })),
    clients: clientsRes.data ?? [],
    milestones: milestonesRes.data ?? [],
    lessonStates: (lessonsRes.data ?? []).map((s) => ({
      lesson_id: s.lesson_id,
      completed: s.completed ?? false,
      reflection: s.reflection as 'yes' | 'no' | null,
      viewed_at: s.viewed_at,
    })),
    streak: {
      current: streakRes.data?.current_streak ?? 0,
      longest: streakRes.data?.longest_streak ?? 0,
    },
  });

  return { data, error: null };
}

export async function generateRecap(period: RecapPeriodInput): Promise<{
  data: RecapRow | null;
  error: { message: string } | null;
  alreadyExisted: boolean;
}> {
  const userId = await getCurrentUserId();

  // Short-circuit if we already have one for this period.
  const existing = await supabase
    .from('recaps')
    .select('*')
    .eq('user_id', userId)
    .eq('kind', period.kind)
    .eq('period_start', period.periodStart)
    .maybeSingle();
  if (existing.data) {
    return { data: existing.data, error: null, alreadyExisted: true };
  }

  const built = await buildRecapData(period);
  if (built.error) {
    return { data: null, error: built.error, alreadyExisted: false };
  }

  const insertRes = await supabase
    .from('recaps')
    .insert({
      user_id: userId,
      kind: period.kind,
      period_start: period.periodStart,
      period_end: period.periodEnd,
      data: built.data as unknown as Record<string, unknown>,
    })
    .select()
    .single();

  if (insertRes.error) return { data: null, error: { message: insertRes.error.message }, alreadyExisted: false };
  invalidate('recaps');
  return { data: insertRes.data, error: null, alreadyExisted: false };
}

/**
 * Re-compute an existing recap's `data` payload and UPDATE the row in
 * place. Useful when the RecapData schema changes (e.g. v1 → v2 with
 * new totals + breakdowns) and the user wants their already-generated
 * recap upgraded without losing the row. Does NOT re-trigger the
 * notify_recap email — that fires on INSERT only.
 *
 * Reads the row's existing kind/period_start/period_end so the caller
 * just needs the recap id.
 */
export async function recomputeRecap(recapId: string): Promise<{
  data: RecapRow | null;
  error: { message: string } | null;
}> {
  const userId = await getCurrentUserId();
  const existing = await supabase
    .from('recaps')
    .select('*')
    .eq('id', recapId)
    .eq('user_id', userId)
    .maybeSingle();
  if (existing.error) return { data: null, error: { message: existing.error.message } };
  if (!existing.data) return { data: null, error: { message: 'Recap not found.' } };

  const built = await buildRecapData({
    kind: existing.data.kind as RecapPeriodInput['kind'],
    periodStart: existing.data.period_start,
    periodEnd: existing.data.period_end,
  });
  if (built.error) return { data: null, error: built.error };

  const updateRes = await supabase
    .from('recaps')
    .update({ data: built.data as unknown as Record<string, unknown> })
    .eq('id', recapId)
    .select()
    .single();

  if (updateRes.error) return { data: null, error: { message: updateRes.error.message } };
  invalidate('recaps');
  return { data: updateRes.data, error: null };
}

/* ============================================================================
 * Feedback — bug reports / feature requests / questions submitted via the
 * Busy mascot helper. Captures URL + user agent + app version client-side
 * so the dev team can repro context-specific bugs.
 * ============================================================================ */

export async function submitFeedback(data: {
  kind: FeedbackKind;
  body: string;
  url?: string;
  userAgent?: string;
  appVersion?: string;
}) {
  const userId = await getCurrentUserId();
  return supabase.from('feedback').insert({
    user_id: userId,
    kind: data.kind,
    body: data.body.trim(),
    url: data.url ?? null,
    user_agent: data.userAgent ?? null,
    app_version: data.appVersion ?? null,
  });
}

export async function completeOnboarding(data: Partial<UserRow>) {
  const userId = await getCurrentUserId();
  const result = await supabase
    .from('users')
    .update({ ...data, onboarded_at: new Date().toISOString() })
    .eq('id', userId)
    .select()
    .single();
  if (!result.error) invalidate('profile');
  return result;
}

/**
 * Set the user's target_hours_per_week and write a row into the
 * user_target_hours history table effective from the current ISO week.
 *
 * Two-write semantics on purpose: users.target_hours_per_week is a cache
 * of the current value (drives the "set your target" CTA detection),
 * user_target_hours is the historical truth that calculations walk.
 *
 * The effective_from_week_start is always Monday of the calling moment's
 * week (in local time), so a target set on Wed applies for the rest of
 * the week and going forward — never retroactively to an earlier week.
 */
export async function setTargetHours(
  targetHours: number,
  /** Intended days per week. Optional; legacy callers that only pass
   *  hours get target_days_per_week left alone on the cache and the
   *  history row inherits the same value the cache currently holds
   *  (or null, which downstream consumers treat as 5). */
  targetDays?: number,
) {
  const userId = await getCurrentUserId();
  const now = new Date();
  // Monday-anchored week start in local time. Mirrors useCapacityUtilization.
  const monday = new Date(now);
  monday.setHours(0, 0, 0, 0);
  const dow = monday.getDay();
  monday.setDate(monday.getDate() - ((dow + 6) % 7));
  const ymd = `${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, '0')}-${String(monday.getDate()).padStart(2, '0')}`;

  const cachePatch: { target_hours_per_week: number; target_days_per_week?: number } = {
    target_hours_per_week: targetHours,
  };
  if (typeof targetDays === 'number') cachePatch.target_days_per_week = targetDays;

  const historyPayload: {
    user_id: string;
    effective_from_week_start: string;
    target_hours: number;
    target_days?: number;
  } = {
    user_id: userId,
    effective_from_week_start: ymd,
    target_hours: targetHours,
  };
  if (typeof targetDays === 'number') historyPayload.target_days = targetDays;

  // Cache update + history insert in parallel — both must succeed for the
  // caller to consider the change committed; we surface either error.
  const [cacheRes, historyRes] = await Promise.all([
    supabase.from('users').update(cachePatch).eq('id', userId).select().single(),
    supabase
      .from('user_target_hours')
      .upsert(historyPayload, { onConflict: 'user_id,effective_from_week_start' })
      .select()
      .single(),
  ]);
  if (cacheRes.error) return { data: null, error: cacheRes.error };
  if (historyRes.error) return { data: null, error: historyRes.error };
  invalidate('profile', 'capacity');
  return { data: cacheRes.data, error: null };
}

export async function updateProfile(data: Partial<UserRow>) {
  const userId = await getCurrentUserId();
  const result = await supabase
    .from('users')
    .update(data)
    .eq('id', userId)
    .select()
    .single();
  if (!result.error) invalidate('profile');
  return result;
}

export async function createVisit(data: {
  client_id: string | null;
  cut_type: string | null;
  price_cents: number;
  /** Default ticket at save time (cut type + add-ons). Persisted alongside the
   *  actual price so we can measure adjustment variance without restricting
   *  the barber's ability to discount or upcharge at save time. */
  default_price_cents?: number | null;
  /** Structured add-ons sold during this cut. Powers per-add-on analytics. */
  addons?: Array<{ id: string; name: string; priceCents: number }>;
  duration_min: number;
  started_at: string;
  ended_at: string;
  /** True when the barber captured BOTH a before AND an after photo for this
   *  cut. Drives the 'transformations' achievement kind via check_achievements. */
  had_before_after?: boolean;
  /** Optional tip in cents (migration 0065). Capped at $500 by the DB
   *  CHECK constraint. Stored separately from price_cents so it doesn't
   *  inflate the revenue achievement counter. */
  tip_cents?: number;
  /** Optional payment method captured at save (migration 0071). Drives
   *  per-method breakdown in recaps for 1099-K reconciliation. Null /
   *  undefined when the barber skipped the picker. */
  payment_method?: PaymentMethod | null;
  /** Whether this cut was a pre-booked appointment (true) or a walk-in
   *  (false). Null/undefined = unspecified (migration 0072). Drives
   *  walk-in % + forecast confidence bands. */
  was_appointment?: boolean | null;
  /** Barber self-rating of this cut (migration 0075). 1 = off today,
   *  2 = solid, 3 = nailed it. Optional — null when the barber
   *  skipped the picker. */
  quality?: CutQuality | null;
  /** Per-phase timing breakdown from the cut timer (migration 0075).
   *  Optional — single-phase services or anonymous walk-ins saved
   *  without timer state can omit it. */
  phase_times?: VisitPhaseTime[] | null;
  /** Total seconds the cut timer was paused during this cut. Defaults
   *  to 0 when not provided. */
  paused_seconds?: number;
  /** Number of pause/resume cycles during this cut. Defaults to 0. */
  pause_count?: number;
  /** Phases skipped via the Skip Phase button. Defaults to 0. */
  phases_skipped?: number;
}) {
  const userId = await getCurrentUserId();
  const result = await supabase
    .from('visits')
    .insert({
      user_id: userId,
      client_id: data.client_id,
      cut_type: data.cut_type,
      price_cents: data.price_cents,
      default_price_cents: data.default_price_cents ?? data.price_cents,
      addons: data.addons ?? [],
      duration_min: data.duration_min,
      started_at: data.started_at,
      ended_at: data.ended_at,
      had_before_after: data.had_before_after ?? false,
      tip_cents: Math.max(0, Math.round(data.tip_cents ?? 0)),
      payment_method: data.payment_method ?? null,
      was_appointment: data.was_appointment ?? null,
      quality: data.quality ?? null,
      phase_times: data.phase_times ?? null,
      paused_seconds: Math.max(0, Math.round(data.paused_seconds ?? 0)),
      pause_count: Math.max(0, Math.round(data.pause_count ?? 0)),
      phases_skipped: Math.max(0, Math.round(data.phases_skipped ?? 0)),
      service_id: null,
      notes: null,
    })
    .select()
    .single();
  if (!result.error) {
    // Visits trigger bump_streak_for_user + check_achievements server-
    // side. Invalidate streak + achievements caches so the ribbon and
    // celebration queue see the new state without a manual refresh.
    invalidate('visits', 'stats', 'appointments', 'streak', 'achievements');
  }
  return result;
}

/** Hard upper bound on what price_cents can be edited to (50000 = $500).
 *  Mirrors the DB trigger guard in migration 0059. Exported so the UI can
 *  render the same cap in helper text + form validation. */
export const VISIT_EDIT_PRICE_CAP_CENTS = 50000;

/** Edit window for visit corrections (24h since started_at). Mirrors the
 *  DB trigger guard in migration 0059. Exported so the Cut Log can decide
 *  whether to render the pencil icon. */
export const VISIT_EDIT_WINDOW_MS = 24 * 60 * 60 * 1000;

/** True if the visit is still inside the 24h edit window. The DB trigger
 *  is the source of truth; this is the client-side gate that decides
 *  whether to even SHOW the edit affordance. */
export function isVisitEditable(visit: Pick<VisitRow, 'started_at'>, now: Date = new Date()): boolean {
  const startedMs = new Date(visit.started_at).getTime();
  return now.getTime() - startedMs <= VISIT_EDIT_WINDOW_MS;
}

/**
 * Edit a visit's user-correctable fields. Enforces the same rules the DB
 * trigger enforces — duration_min down-only, price_cents capped at $500 —
 * with friendlier client-side errors before the round trip. The DB is
 * still the source of truth; any client-side bypass attempt is rejected
 * by the visits_update_guard trigger.
 *
 * Allowed patch fields: price_cents, duration_min, cut_type, notes.
 * Locked fields (started_at, had_before_after) are NOT in the patch type.
 */
export async function updateVisit(
  visitId: string,
  current: Pick<VisitRow, 'started_at' | 'duration_min'>,
  patch: {
    price_cents?: number;
    duration_min?: number;
    cut_type?: string | null;
    notes?: string | null;
    /** Backfill or correct the payment method on an existing visit
     *  inside the 24h edit window. Pass null to clear back to
     *  "Unspecified." */
    payment_method?: PaymentMethod | null;
    /** Backfill or correct the tip amount on an existing visit. */
    tip_cents?: number;
    /** Backfill walk-in vs appointment within the 24h edit window. */
    was_appointment?: boolean | null;
    /** Backfill or correct the self-rated cut quality within the
     *  24h edit window. Other cut-timer signals (phase_times,
     *  paused_seconds, pause_count, phases_skipped) are intentionally
     *  NOT editable — they're machine-captured during the cut and
     *  shouldn't be hand-corrected after the fact. */
    quality?: CutQuality | null;
  },
) {
  // Client-side gate: same window the trigger enforces. Surface a
  // friendly error instead of round-tripping for a guaranteed reject.
  if (!isVisitEditable(current)) {
    return {
      data: null,
      error: { message: 'This cut is older than 24 hours and can no longer be edited.' },
    };
  }
  if (typeof patch.price_cents === 'number') {
    if (patch.price_cents < 0) {
      return { data: null, error: { message: 'Price cannot be negative.' } };
    }
    if (patch.price_cents > VISIT_EDIT_PRICE_CAP_CENTS) {
      return {
        data: null,
        error: { message: 'Edited price is capped at $500.' },
      };
    }
  }
  if (
    typeof patch.duration_min === 'number' &&
    typeof current.duration_min === 'number' &&
    patch.duration_min > current.duration_min
  ) {
    return {
      data: null,
      error: { message: 'Cut length can only be reduced, not increased.' },
    };
  }

  const result = await supabase
    .from('visits')
    .update(patch)
    .eq('id', visitId)
    .select()
    .single();
  if (!result.error) {
    // Edits can shift Revenue (via original_price_cents ratchet) and
    // Timekeeper counters; invalidate the same keys createVisit hits
    // so badge state + dashboards see the new numbers.
    invalidate('visits', 'stats', 'appointments', 'achievements');
  }
  return result;
}

/* ============================================================================
 * Dashboard hooks
 *
 * Range model: a `RangeKey` like '7d' / '30d' / 'ytd' resolves to a `[start, end]`
 * window. All dashboard hooks accept a range so the metric grid, chart, and
 * insights stay in sync as the user changes the selector.
 * ============================================================================ */

export type RangeKey = 'today' | '7d' | '14d' | '30d' | '90d' | 'ytd' | 'custom';

/** Custom-range bounds — both YYYY-MM-DD inclusive. Pairs with
 *  `range === 'custom'`; hooks throw / fall back when range is
 *  'custom' but no bounds are supplied. Max 366 days from start
 *  to end (enforced UI-side via clampCustomRange). */
export type CustomRangeBounds = { start: string; end: string };

/** Hard cap on custom-range window — keeps the day-by-day chart from
 *  growing past ~1 year of bars and bounds the query work. UI clamps
 *  on date pick. */
export const CUSTOM_RANGE_MAX_DAYS = 366;

/** Trim a custom range to the supported bounds: end >= start, end <=
 *  today, total span <= CUSTOM_RANGE_MAX_DAYS. Returns the clamped
 *  pair. Caller decides whether to display a warning. */
export function clampCustomRange(input: { start: string; end: string }): CustomRangeBounds {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayStr = ymdLocal(today);
  let s = input.start;
  let e = input.end;
  if (e > todayStr) e = todayStr;
  if (s > e) s = e;
  // Enforce max span by walking forward from start. Most natural for
  // user intent ("I picked Jan 1 to Dec 31, give me at most a year").
  const startDate = new Date(s + 'T00:00:00');
  const endDate = new Date(e + 'T00:00:00');
  const days = Math.round((endDate.getTime() - startDate.getTime()) / 86400000) + 1;
  if (days > CUSTOM_RANGE_MAX_DAYS) {
    const capped = new Date(startDate);
    capped.setDate(capped.getDate() + (CUSTOM_RANGE_MAX_DAYS - 1));
    e = ymdLocal(capped);
  }
  return { start: s, end: e };
}

function ymdLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function rangeStartDate(
  range: RangeKey,
  custom?: CustomRangeBounds | null,
): Date {
  if (range === 'custom') {
    // No bounds passed for 'custom' → fall back to "today" so callers
    // that forget to thread the bounds don't crash. The Dashboard UI
    // never hits this branch (always supplies bounds with 'custom').
    if (custom) return new Date(custom.start + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  switch (range) {
    case 'today':
      return start;
    case '7d':
      start.setDate(start.getDate() - 6);
      return start;
    case '14d':
      start.setDate(start.getDate() - 13);
      return start;
    case '30d':
      start.setDate(start.getDate() - 29);
      return start;
    case '90d':
      start.setDate(start.getDate() - 89);
      return start;
    case 'ytd': {
      const now = new Date();
      return new Date(now.getFullYear(), 0, 1);
    }
  }
}

/** Range end (inclusive Date at 23:59:59 local). Defaults to "now"
 *  for preset ranges; for 'custom' it's the user-picked end date.
 *  Caller uses this for the closing bound of queries — most existing
 *  hooks default to "now" so they don't need this for presets. */
export function rangeEndDate(
  range: RangeKey,
  custom?: CustomRangeBounds | null,
): Date {
  if (range === 'custom' && custom) {
    const end = new Date(custom.end + 'T23:59:59.999');
    return end;
  }
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  return end;
}

/**
 * Half-open window [start, endExclusive) representing the period
 * IMMEDIATELY before rangeStartDate(range) — used by the dashboard's
 * +/- delta math so each metric tile shows "X more than the previous
 * period." Length matches the current range exactly so the comparison
 * is apples-to-apples; the only exception is 'ytd', which compares
 * to the same Jan 1 → today window from the prior year.
 *
 * Returns null for 'custom' — there's no canonical "prior period" for
 * an arbitrary date range (same-length-before? calendar-aligned prior
 * year?). Per the design choice, the dashboard suppresses vs-prior
 * chips entirely when a custom range is active.
 */
export function priorRangeBounds(
  range: RangeKey,
  custom?: CustomRangeBounds | null,
): { start: Date; endExclusive: Date } | null {
  if (range === 'custom') return null;
  const currentStart = rangeStartDate(range, custom);
  if (range === 'ytd') {
    const start = new Date(currentStart);
    start.setFullYear(start.getFullYear() - 1);
    const endExclusive = new Date();
    endExclusive.setHours(0, 0, 0, 0);
    endExclusive.setDate(endExclusive.getDate() + 1); // tomorrow this year
    endExclusive.setFullYear(endExclusive.getFullYear() - 1); // → same date last year + 1
    return { start, endExclusive };
  }
  const lenDays =
    range === 'today' ? 1
      : range === '7d' ? 7
      : range === '14d' ? 14
      : range === '30d' ? 30
      : /* 90d */ 90;
  const start = new Date(currentStart);
  start.setDate(start.getDate() - lenDays);
  return { start, endExclusive: currentStart };
}

export const RANGE_LABELS: Record<RangeKey, string> = {
  today: 'Today',
  '7d': 'Last 7 days',
  '14d': 'Last 14 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
  ytd: 'Year to date',
  custom: 'Custom range',
};

export type DashboardMetrics = {
  /** Cut + add-on revenue. Tips live in `total_tips_cents` separately
   *  so the headline Revenue metric stays anchored to service economics
   *  the barber controls (cut price + add-on attach), not tip variance
   *  that depends on the client. The Revenue card adds them together
   *  for display but the breakdown modal exposes the split. */
  total_revenue_cents: number;
  total_tips_cents: number;
  total_visits: number;
  avg_ticket_cents: number;
  /** Count of clients whose `created_at` falls inside the current
   *  range — i.e. clients added during the period. Distinct from
   *  client_tier='new' (which is a moving classification of recent
   *  clients, regardless of when they were added). */
  new_clients: number;
  /** Prior-period equivalents — same metric computed over the window
   *  IMMEDIATELY before the current range. Used by the metric tiles to
   *  show absolute +/- deltas alongside the current value. */
  prior_total_revenue_cents: number;
  prior_total_tips_cents: number;
  prior_total_visits: number;
  prior_avg_ticket_cents: number;
  prior_new_clients: number;
};

export function useDashboardMetrics(
  range: RangeKey = '30d',
  custom?: CustomRangeBounds | null,
) {
  return useFetch<DashboardMetrics>(
    async () => {
      const start = rangeStartDate(range, custom);
      const end = rangeEndDate(range, custom);
      const prior = priorRangeBounds(range, custom);

      // Four parallel queries:
      //  1. Visits in the current range (revenue + count)
      //  2. Visits in the prior range (revenue + count, for deltas) —
      //     skipped on 'custom' since there's no canonical prior period
      //  3. ALL eligible visits ever — ordered ASC so the first row
      //     per client_id is that client's first eligible haircut.
      //     This drives the new-clients metric.
      //  4. All clients with first_name — needed to filter out the
      //     auto-generated "Anonymous" walk-in handle.
      //
      // The new-clients definition is: "clients whose FIRST eligible
      // (≥6 min, non-Anonymous) haircut fell within the selected
      // range." This replaces the prior contact-creation count, which
      // inflated the metric whenever a barber imported a CSV of
      // contacts. The new definition mirrors the Clients achievement
      // counter so progress on both tiles aligns.
      let currentVisitsQuery = supabase
        .from('visits')
        .select('price_cents, tip_cents')
        .gte('started_at', start.toISOString());
      if (range === 'custom') {
        currentVisitsQuery = currentVisitsQuery.lte('started_at', end.toISOString());
      }
      // Build the parallel-fetch set. Skip the prior-period query
      // entirely when there's no prior to compare to (custom range);
      // the result slot is just an empty array.
      const [currentVisitsRes, priorVisitsRes, allEligibleVisitsRes, clientsRes] =
        await Promise.all([
          currentVisitsQuery,
          prior
            ? supabase
                .from('visits')
                .select('price_cents, tip_cents')
                .gte('started_at', prior.start.toISOString())
                .lt('started_at', prior.endExclusive.toISOString())
            : Promise.resolve({
                data: [] as Array<{ price_cents: number | null; tip_cents: number | null }>,
                error: null as { message: string } | null,
              }),
          supabase
            .from('visits')
            .select('client_id, started_at')
            .gte('duration_min', 6)
            .not('client_id', 'is', null)
            .order('started_at', { ascending: true }),
          supabase.from('clients').select('id, first_name'),
        ]);
      const firstErr =
        currentVisitsRes.error ||
        priorVisitsRes.error ||
        allEligibleVisitsRes.error ||
        clientsRes.error;
      if (firstErr) return { data: null, error: firstErr };

      const currentVisits = currentVisitsRes.data ?? [];
      const priorVisits = priorVisitsRes.data ?? [];
      const total_revenue_cents = currentVisits.reduce(
        (sum, v) => sum + (v.price_cents ?? 0),
        0,
      );
      // Tips summed independently of revenue. tip_cents column landed in
      // migration 0065 and defaults to 0, so historical rows aggregate
      // cleanly without NULL handling.
      const total_tips_cents = currentVisits.reduce(
        (sum, v) => sum + (v.tip_cents ?? 0),
        0,
      );
      const total_visits = currentVisits.length;
      const avg_ticket_cents =
        total_visits > 0 ? Math.round(total_revenue_cents / total_visits) : 0;
      const prior_total_revenue_cents = priorVisits.reduce(
        (sum, v) => sum + (v.price_cents ?? 0),
        0,
      );
      const prior_total_tips_cents = priorVisits.reduce(
        (sum, v) => sum + (v.tip_cents ?? 0),
        0,
      );
      const prior_total_visits = priorVisits.length;
      const prior_avg_ticket_cents =
        prior_total_visits > 0
          ? Math.round(prior_total_revenue_cents / prior_total_visits)
          : 0;

      // First-visit-per-client lookup. Anonymous walk-ins are excluded
      // (same filter the Clients achievement uses). The eligible-visits
      // query above is ordered ASC, so the FIRST row encountered for
      // any given client_id is that client's earliest qualifying visit.
      const validClientIds = new Set(
        (clientsRes.data ?? [])
          .filter((c) => c.first_name == null || c.first_name !== 'Anonymous')
          .map((c) => c.id),
      );
      const firstVisitByClient = new Map<string, string>();
      for (const v of allEligibleVisitsRes.data ?? []) {
        if (!v.client_id || !validClientIds.has(v.client_id)) continue;
        if (!firstVisitByClient.has(v.client_id)) {
          firstVisitByClient.set(v.client_id, v.started_at);
        }
      }
      const currentStartIso = start.toISOString();
      const currentEndIso = end.toISOString();
      const priorStartIso = prior?.start.toISOString() ?? null;
      const priorEndIso = prior?.endExclusive.toISOString() ?? null;
      let new_clients = 0;
      let prior_new_clients = 0;
      for (const firstAt of firstVisitByClient.values()) {
        // Custom ranges have a closing bound; preset ranges do not (extend
        // through "now") — the chart hooks already enforce this distinction.
        if (range === 'custom') {
          if (firstAt >= currentStartIso && firstAt <= currentEndIso) new_clients += 1;
        } else {
          if (firstAt >= currentStartIso) new_clients += 1;
        }
        if (priorStartIso != null && priorEndIso != null) {
          if (firstAt >= priorStartIso && firstAt < priorEndIso) prior_new_clients += 1;
        }
      }

      return {
        data: {
          total_revenue_cents,
          total_tips_cents,
          total_visits,
          avg_ticket_cents,
          new_clients,
          prior_total_revenue_cents,
          prior_total_tips_cents,
          prior_total_visits,
          prior_avg_ticket_cents,
          prior_new_clients,
        },
        error: null,
      };
    },
    [range, custom?.start ?? '', custom?.end ?? ''],
    ['visits', 'clients'],
  );
}

/**
 * Capacity utilization snapshot for the dashboard tile.
 *
 * Pulls YTD visits in one query and crunches the numbers locally — no new
 * server-side view needed. The math is honest about edge cases:
 *   - When the user has no target set, the missed-revenue + potential-annual
 *     numbers are null (the tile renders a "set your target" CTA instead).
 *   - When this_week_actual_hours is 0, this_week_revenue_per_hour falls
 *     back to the YTD rate so a fresh-week barber still sees a sensible
 *     missed-revenue figure.
 *   - When ytd_hours is 0 (no logged cuts yet), all rates are 0 — the tile
 *     should show the "log your first timed cut" empty state.
 *   - Annual projection is straight YTD pace × (52 / weeks_elapsed).
 *     Caller decides when to caveat (under 4 weeks of YTD data).
 */
export type CapacityUtilization = {
  /** Cached current target from users.target_hours_per_week. Null = not set. */
  target_hours_per_week: number | null;
  /** Cached current days-per-week target. Null when the user hasn't set
   *  it yet; UI consumers should fall back to 5 (matches the migration
   *  backfill default and the most common barber schedule). */
  target_days_per_week: number | null;
  /** Convenience: target_hours_per_week / target_days_per_week (using
   *  the 5-day fallback when days are unset). Null when hours-per-week
   *  itself is unset. Drives the "your daily target is Xh" surface on
   *  the capacity tile + future per-day progress on Today. */
  daily_target_hours: number | null;
  /** What the barber considers a "fully utilized" clock-hour, 10–100
   *  in 10% steps. Drives the per-hour percentage math + heatmap
   *  shading. See users.hourly_target_pct comment in 0058. */
  hourly_target_pct: number;

  // ─── this week (Mon-anchored, local calendar) ─────────────────────────
  /** Target effective for THIS week — looked up from user_target_hours,
   *  not necessarily the same as target_hours_per_week if the user
   *  changed their target this week. */
  this_week_target_hours: number | null;
  this_week_actual_hours: number;
  this_week_revenue_cents: number;
  this_week_visit_count: number;
  /** target − actual, clamped to 0. Null when target is not set. */
  this_week_gap_hours: number | null;
  /** gap_hours × revenue_per_hour. Null when target not set OR sample
   *  is too small to project (under 4 timed cuts this week → null). */
  this_week_missed_cents: number | null;

  // ─── YTD ──────────────────────────────────────────────────────────────
  ytd_actual_hours: number;
  ytd_revenue_cents: number;
  ytd_visit_count: number;
  ytd_weeks_elapsed: number;
  /** total ytd revenue / total ytd hours (cents per hour). 0 when ytd_hours = 0. */
  ytd_revenue_per_hour_cents: number;

  // ─── annual outlook ───────────────────────────────────────────────────
  /** ytd_revenue × (52 / weeks_elapsed). 0 when no data. */
  projected_annual_cents: number;
  /** rate × target × 52, where rate uses the SELECTED RANGE'S $/hr
   *  when available and falls back to YTD. Picks up hourly_target_pct
   *  via annual_target_hours so a barber dialing their target down
   *  sees a more achievable potential. Null when target not set. */
  potential_annual_cents: number | null;
  /** potential − projected, clamped to 0. Null when target not set. */
  annual_gap_cents: number | null;
  /** Hours of cuts targeted across a full year, after applying
   *  hourly_target_pct (e.g. 40h × 52 × 0.80 = 1664). Null when no
   *  weekly target set. */
  annual_target_hours: number | null;

  /** True until 4+ weeks of YTD data exist. Caller adds a "firms up" caveat. */
  early_year_projection: boolean;

  // ─── range-bounded earnings outlook ─────────────────────────────────
  /** Total revenue earned inside the selected range (every visit). */
  period_revenue_cents: number;
  /** Sum of duration_min/60 for visits inside the selected range
   *  (every visit). */
  period_actual_hours: number;
  /** Total revenue inside the range, RATEABLE subset only — i.e.
   *  cuts with duration_min ≥ 6 (the earning-rate floor). Drives the
   *  numerator shown in the Earnings Outlook explainer so the math the
   *  user sees matches the $/hr rate they're being shown. */
  period_revenue_rateable_cents: number;
  /** Sum of duration_min/60 for the same rateable subset. Drives the
   *  denominator shown in the Earnings Outlook explainer. */
  period_actual_hours_rateable: number;
  /** period_revenue_rateable / period_actual_hours_rateable. Null when
   *  there are no rateable hours to derive a rate from. This is the
   *  "honest" $/hour figure the rest of the outlook math uses for
   *  projections. */
  period_dollar_per_hour_cents: number | null;
  /** Targeted hours for the period: weekly_target × (rangeDays/7) ×
   *  (hourly_target_pct / 100). Null when no weekly target set. */
  period_target_hours: number | null;
  /** period_dollar_per_hour × period_target_hours — what the barber
   *  could earn in the range at their current rate + chosen capacity
   *  target. Null when either dependency is null. */
  period_potential_cents: number | null;
  /** period_potential − period_revenue, clamped to 0. Null when
   *  potential isn't computable. */
  period_gap_cents: number | null;

  // ─── this-week activity heatmap ───────────────────────────────────────
  /** 7 days × 16 hours (6am–10pm) of activity state. Each hour is one of:
   *    'active' — any cut overlapped this hour (timer was running)
   *    'gap'    — hour falls within the barber's typical working window
   *               for this day-of-week (derived from last 4 weeks of
   *               history) but NO cut was logged. The opportunity slots.
   *    'off'    — hour falls outside the typical working window. No
   *               judgment — they were just off the clock.
   *  Null when there's not enough history yet (under a few weeks of
   *  visits) — caller renders an empty state instead of a misleading
   *  all-gray heatmap. */
  this_week_heatmap: WeekHeatmap | null;
  /** Sum of 'gap' hours this week × ytd_revenue_per_hour. The dollar
   *  figure shown in the opportunity copy. Null when ytd visit count is
   *  too small to back the rate estimate (under 8 cuts) or there's no
   *  heatmap yet. */
  this_week_opportunity_cents: number | null;
  /** All weeks from yearStart up to and including this week,
   *  chronological (oldest first → this-week last). Powers the swipe-
   *  back-up-to-a-year heatmap on the dashboard. Empty when there's
   *  not enough history to derive working windows. */
  weekly_heatmaps: WeekHeatmap[];
};

export type DayHeatmap = {
  /** Local-day start ms. */
  day_ms: number;
  /** Day-of-week 0=Sun … 6=Sat. */
  dow: number;
  /** True if this is today's calendar day. The tile shows a subtle
   *  highlight on today's row. */
  is_today: boolean;
  /** Utilization percentage per hour (6am, 7am, …, 9pm — 16 entries).
   *  0 = no cuts in that hour ("off"). 1+ = some cuts, expressed as
   *  percentage of the barber's hourly target (60 × hourly_target_pct
   *  ÷ 100 minutes). Caps unbounded — a freakishly busy hour might
   *  read 130% if it exceeds the target; the heatmap component clamps
   *  to the top color bucket. */
  hours: number[];
};

export type WeekHeatmap = {
  /** Mon-anchored. days[0] = Monday, days[6] = Sunday. */
  days: DayHeatmap[];
  /** Hour displayed by hours[0] in each day. Always 6 for now (6am). */
  hour_start: number;
  /** Exclusive upper bound. 22 means hours[15] is 9pm (9–10pm slot). */
  hour_end: number;
  /** Monday 00:00 ms for this week — used by the UI for week labels and
   *  ordering when paging through history. */
  week_start_ms: number;
  /** Total number of red ('gap') hours that week. Drives both the
   *  opportunity-dollar readout for this week AND the historical
   *  per-week recap when the user pages back. */
  gap_hours: number;
  /** gap_hours × YTD revenue/hour. Null when YTD visit count is too
   *  small to back the rate (under OPPORTUNITY_MIN_YTD_VISITS). */
  missed_cents: number | null;
};

const HEATMAP_HOUR_START = 6;
const HEATMAP_HOUR_END = 22; // exclusive — yields 16 hour-slots
const HEATMAP_HOURS = HEATMAP_HOUR_END - HEATMAP_HOUR_START;
/** Minimum logged visits before the heatmap is worth rendering. Without
 *  this floor, the first cut of a brand-new account would show a single
 *  green block in an otherwise gray week — informative for nobody. */
const HEATMAP_MIN_VISITS = 4;
/** Per-hour revenue rate is too unstable below this YTD volume. We still
 *  render the heatmap (it's an activity tracker, not a stats projection)
 *  but skip the "$X capacity below target" dollar figure until the rate
 *  has enough sample behind it. */
const OPPORTUNITY_MIN_YTD_VISITS = 8;

/** Returns capacity-utilization snapshot for the current user.
 *
 *  History-aware: each week's hours are compared to the target effective
 *  WHEN the week happened (walks user_target_hours, not just users.target_
 *  hours_per_week). Vacation-period exclusion was removed in 0052 — the
 *  Streak Shield overhaul replaced vacation mode with shields, and the
 *  capacity tile now shows all weeks straight without exclusions.
 */
export function useCapacityUtilization(
  range: RangeKey = 'ytd',
  custom?: CustomRangeBounds | null,
) {
  return useFetch<CapacityUtilization>(
    async () => {
      const now = new Date();
      const yearStart = new Date(now.getFullYear(), 0, 1);
      const yearStartIso = yearStart.toISOString();

      // Range-bounded window used by the new period/annual outlook
      // math. We still pull full YTD visits for the heatmap + projection
      // math but bucket the range slice separately for $/hour.
      const rangeStartMs = rangeStartDate(range, custom).getTime();
      // For 'custom', use the picked end date as the closing bound; for
      // presets, the period extends through "now" (already encoded in
      // the existing math via now.getTime() comparisons downstream).
      const customEndMs =
        range === 'custom' && custom ? rangeEndDate(range, custom).getTime() : null;
      const rangeDays =
        range === 'today'  ? 1
        : range === '7d'   ? 7
        : range === '14d'  ? 14
        : range === '30d'  ? 30
        : range === '90d'  ? 90
        : range === 'custom' && custom
          ? Math.max(
              1,
              Math.ceil(
                (new Date(custom.end + 'T23:59:59').getTime() -
                  new Date(custom.start + 'T00:00:00').getTime()) /
                  (24 * 60 * 60 * 1000),
              ),
            )
        : /* ytd */ Math.max(1, Math.ceil((now.getTime() - yearStart.getTime()) / (24 * 60 * 60 * 1000)));
      // Silence unused warning — customEndMs is consumed below.
      void customEndMs;

      // Monday-anchored "this week" — runs Mon-Sun in local time.
      const thisWeekStart = new Date(now);
      thisWeekStart.setHours(0, 0, 0, 0);
      const dow = thisWeekStart.getDay();
      thisWeekStart.setDate(thisWeekStart.getDate() - ((dow + 6) % 7));
      const thisWeekStartMs = thisWeekStart.getTime();

      // Pull profile (current target), visit history, and target history.
      const [profileRes, visitsRes, historyRes] = await Promise.all([
        supabase
          .from('users')
          .select('target_hours_per_week, target_days_per_week, hourly_target_pct')
          .single(),
        supabase
          .from('visits')
          .select('price_cents, duration_min, started_at')
          .gte('started_at', yearStartIso),
        supabase
          .from('user_target_hours')
          .select('effective_from_week_start, target_hours')
          .order('effective_from_week_start', { ascending: true }),
      ]);
      if (profileRes.error) return { data: null, error: profileRes.error };
      if (visitsRes.error) return { data: null, error: visitsRes.error };
      if (historyRes.error) return { data: null, error: historyRes.error };

      const currentTarget = profileRes.data?.target_hours_per_week ?? null;
      const currentDays = profileRes.data?.target_days_per_week ?? null;
      // Daily target uses the 5-day fallback when days are unset so a
      // user who only set hours still gets a sensible per-day number.
      const dailyTargetHours =
        currentTarget != null && currentTarget > 0
          ? currentTarget / (currentDays && currentDays > 0 ? currentDays : 5)
          : null;
      // Hourly target percentage drives per-cell heatmap utilization.
      // 100% means "60 minutes of cuts is a full hour"; 80% means 48
      // minutes is enough. Defaults to 100 (legacy behavior) so users
      // who never touch the setting see the same shading as before.
      const hourlyTargetPct = profileRes.data?.hourly_target_pct ?? 100;
      const hourTargetMin = Math.max(1, 60 * (hourlyTargetPct / 100));
      const ytdVisits = visitsRes.data ?? [];
      const targetHistory = historyRes.data ?? [];

      // Resolve the applicable target for a given week start (ms). Walks
      // history newest-first; first match wins.
      const targetForWeek = (weekStartMs: number): number | null => {
        for (let i = targetHistory.length - 1; i >= 0; i--) {
          const h = targetHistory[i]!;
          // effective_from_week_start is a date string; parse as local Mon.
          const effMs = new Date(h.effective_from_week_start + 'T00:00:00').getTime();
          if (effMs <= weekStartMs) return h.target_hours;
        }
        return null;
      };

      // Bucket visits by Mon-anchored week start (ms key).
      //
      // EARNING-RATE FLOOR: cuts under 6 minutes are still saved and
      // displayed everywhere, but they don't count toward $/hr math —
      // a sub-6-min cut at $50 extrapolates to $500+/hr and drags any
      // small-sample rate sideways. We accumulate two parallel sums:
      //   - raw {hours, revenue}        → all visits, for display
      //                                    counts and the weekly target
      //                                    progress bar
      //   - rateable {hours, revenue}   → only visits with
      //                                    duration_min >= 6, used to
      //                                    compute every $/hr figure on
      //                                    the tile (ytd rate, period
      //                                    rate, missed-revenue projections)
      const RATE_MIN_DURATION = 6;
      const weekBuckets = new Map<number, { hours: number; revenue: number; count: number }>();
      let ytdHours = 0;
      let ytdRevenue = 0;
      let ytdHoursRateable = 0;
      let ytdRevenueRateable = 0;
      const ytdCount = ytdVisits.length;
      for (const v of ytdVisits) {
        const durMin = v.duration_min ?? 0;
        const hours = durMin / 60;
        const revenue = v.price_cents ?? 0;
        ytdHours += hours;
        ytdRevenue += revenue;
        if (durMin >= RATE_MIN_DURATION) {
          ytdHoursRateable += hours;
          ytdRevenueRateable += revenue;
        }
        const startedMs = new Date(v.started_at).getTime();
        const d = new Date(startedMs);
        d.setHours(0, 0, 0, 0);
        const dDow = d.getDay();
        d.setDate(d.getDate() - ((dDow + 6) % 7));
        const wsMs = d.getTime();
        const cur = weekBuckets.get(wsMs) ?? { hours: 0, revenue: 0, count: 0 };
        cur.hours += hours;
        cur.revenue += revenue;
        cur.count += 1;
        weekBuckets.set(wsMs, cur);
      }
      const thisWeek = weekBuckets.get(thisWeekStartMs) ?? { hours: 0, revenue: 0, count: 0 };
      const thisWeekTarget = targetForWeek(thisWeekStartMs);
      const ytdRevPerHourCents =
        ytdHoursRateable > 0 ? Math.round(ytdRevenueRateable / ytdHoursRateable) : 0;

      // ─── Range-bounded period stats ──────────────────────────────────────
      // Powers the "Earnings outlook" section. Display numbers (Earned /
      // Potential / Gap) keep every visit so they match the dashboard's
      // Revenue tile, but the $/hr rate filters to rateable visits only
      // (≥6 min) for denominator stability.
      let periodRevenueCents = 0;
      let periodActualHours = 0;
      let periodRevenueRateable = 0;
      let periodActualHoursRateable = 0;
      for (const v of ytdVisits) {
        const startedMs = new Date(v.started_at).getTime();
        if (startedMs < rangeStartMs) continue;
        // Custom ranges have a closing bound; preset ranges run through
        // "now" and don't need an upper clamp here.
        if (customEndMs != null && startedMs > customEndMs) continue;
        const durMin = v.duration_min ?? 0;
        periodActualHours += durMin / 60;
        periodRevenueCents += v.price_cents ?? 0;
        if (durMin >= RATE_MIN_DURATION) {
          periodActualHoursRateable += durMin / 60;
          periodRevenueRateable += v.price_cents ?? 0;
        }
      }
      const periodDollarPerHourCents =
        periodActualHoursRateable > 0
          ? Math.round(periodRevenueRateable / periodActualHoursRateable)
          : null;
      // Target hours for the SELECTED PERIOD = weekly target × range
      // weeks × hourly_target_pct fraction. The pct shrinks the bar
      // ("don't pretend you'll cut 60 min/hr") so a barber who picks
      // 80% sees a lower-but-more-achievable potential.
      // Period target hours — the "ceiling" we measure Potential
      // against. For ranges of 7+ days, `weekly × (rangeDays / 7)` is
      // the right math: mathematically equivalent to (daily target ×
      // expected workdays in range), since off-days average out across
      // a full week or longer.
      //
      // For range='today' the user has explicitly chosen "today" as a
      // workday — they want the DAILY target, not an expected-value
      // smear that treats today as e.g. 5/7 likely to be a workday.
      // So we special-case 'today' and use (weekly / days_per_week).
      // Keeps Period Potential consistent with the daily progress bar
      // that surfaces on the same range.
      const rangeWeeks = rangeDays / 7;
      const periodTargetHoursAtFull =
        currentTarget != null && currentTarget > 0
          ? range === 'today'
            ? currentTarget / Math.max(1, currentDays ?? 5)
            : currentTarget * rangeWeeks
          : null;
      const periodTargetHours =
        periodTargetHoursAtFull != null
          ? periodTargetHoursAtFull * (hourlyTargetPct / 100)
          : null;
      const periodPotentialCents =
        periodDollarPerHourCents != null && periodTargetHours != null
          ? Math.round(periodDollarPerHourCents * periodTargetHours)
          : null;
      const periodGapCents =
        periodPotentialCents != null
          ? Math.max(0, periodPotentialCents - periodRevenueCents)
          : null;
      // Annual target hours = full year at the same hourly_target_pct.
      // Used for the year-end potential — feeds the rate forward.
      const annualTargetHours =
        currentTarget != null && currentTarget > 0
          ? currentTarget * 52 * (hourlyTargetPct / 100)
          : null;

      // Weeks-elapsed denominator kept for legacy fields that still
      // reference it (ytd_weeks_elapsed is exposed on the snapshot for
      // callers like the rate-per-hour math). Vacation-week subtraction
      // was removed when shields replaced vacation mode in 0052.
      const msPerWeek = 7 * 24 * 60 * 60 * 1000;
      const weeksElapsed = Math.max(
        1,
        (now.getTime() - yearStart.getTime()) / msPerWeek,
      );

      // Year-end projection — now driven by the SELECTED RANGE, not
      // hardcoded to YTD. Formula: (revenue in range ÷ days in range)
      // × 365. Mathematically equivalent to the old YTD-based formula
      // when range='ytd' (off by ~1/365), but it now responds to the
      // dashboard range selector like every other number on the
      // Capacity tile.
      //
      // Uses ALL revenue (not just rateable cuts ≥6 min) — the 6-min
      // floor + $500/visit cap exist for anti-gaming in achievement
      // counters; projected revenue should reflect the barber's
      // actual money.
      const projectedAnnualCents = Math.round((periodRevenueCents / Math.max(1, rangeDays)) * 365);
      // "Firms up after a few weeks" caveat — now triggers when the
      // SELECTED window is small (≤ 30 days) so the noise inherent in
      // a tiny sample is flagged. Boundary lines up with the 30d chip.
      const earlyYearProjection = rangeDays < 30;

      // Per-target calcs only fire when this week has a target.
      // The gap is measured against the EFFECTIVE target (raw weekly
      // hours × hourly_target_pct%), matching what the progress bar
      // on the Capacity tile displays. Without the realism multiplier,
      // a barber on Target 80% who cut for exactly their 32-effective-
      // hours target would still see "8h gap remaining" — wrong.
      let weekGapHours: number | null = null;
      let weekMissedCents: number | null = null;
      let potentialAnnualCents: number | null = null;
      let annualGapCents: number | null = null;
      if (thisWeekTarget != null && thisWeekTarget > 0) {
        const effectiveWeekTarget = thisWeekTarget * (hourlyTargetPct / 100);
        weekGapHours = Math.max(0, effectiveWeekTarget - thisWeek.hours);
        if (thisWeek.count >= 4 && ytdRevPerHourCents > 0) {
          weekMissedCents = Math.round(weekGapHours * ytdRevPerHourCents);
        }
      }
      // Annual potential uses the RANGE's $/hour (so picking a different
      // range moves the projection — "if this 30-day rate held for the
      // year") × annualTargetHours (which already bakes in
      // hourly_target_pct). Falls back to YTD rate when the range has
      // no actuals to derive a rate from.
      const rateForAnnualCents = periodDollarPerHourCents ?? ytdRevPerHourCents;
      if (annualTargetHours != null && rateForAnnualCents > 0) {
        potentialAnnualCents = Math.round(rateForAnnualCents * annualTargetHours);
        annualGapCents = Math.max(0, potentialAnnualCents - projectedAnnualCents);
      }

      // ─── Activity heatmap: 7 days × 16 hours of "when was I cutting?"
      //
      // No pattern detection, no working-window inference. Each clock-
      // hour is just: had-cuts (active/partial) or didn't (off). The
      // "missed capacity" dollar figure that used to come from counting
      // empty in-window cells now comes from the per-week target minus
      // per-week actual hours — same intent, more honest math.
      let heatmap: WeekHeatmap | null = null;
      let totalGapHours = 0;
      const weeklyHeatmaps: WeekHeatmap[] = [];
      if (ytdVisits.length >= HEATMAP_MIN_VISITS) {
        // Index cuts by their local calendar-day key (YYYY-MM-DD) for
        // O(1) per-day lookup below.
        const cutsByDay = new Map<string, Array<{ startMs: number; endMs: number }>>();
        const dayKey = (ms: number): string => {
          const d = new Date(ms);
          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        };
        for (const v of ytdVisits) {
          const startMs = new Date(v.started_at).getTime();
          const endMs = startMs + (v.duration_min ?? 0) * 60_000;
          const k = dayKey(startMs);
          const list = cutsByDay.get(k) ?? [];
          list.push({ startMs, endMs });
          cutsByDay.set(k, list);
        }

        const todayKey = dayKey(now.getTime());

        // Sliding 7-day window anchored on TODAY (not Mon–Sun). The
        // latest window covers [today - 6 days, today]; older windows
        // step back in 7-day chunks. This matches what the rest of the
        // dashboard means by "Last 7 days" — the revenue-by-day chart
        // uses the same `rangeStartDate(range)` → today window, so the
        // heatmap now shows the same date range as that chart and the
        // user's mental model of "range = last N days" stays consistent.
        const todayStart = new Date(now);
        todayStart.setHours(0, 0, 0, 0);
        const latestWindowStartMs = todayStart.getTime() - 6 * 24 * 60 * 60 * 1000;

        // How many 7-day windows fit between yearStart and the latest
        // window. Cap at 52 so an early-Jan barber who crossed years
        // doesn't blow past the year limit.
        const yearStartDayMs = (() => {
          const d = new Date(yearStart);
          d.setHours(0, 0, 0, 0);
          return d.getTime();
        })();
        const weeksSpan = Math.min(
          52,
          Math.max(
            0,
            Math.floor((latestWindowStartMs - yearStartDayMs) / msPerWeek),
          ),
        );

        // Per-week computation. Each hour is purely activity-based; the
        // gap_hours + missed_cents fields on the resulting WeekHeatmap
        // come from the target-vs-actual comparison for that week, not
        // from counting empty cells.
        const buildWeekHeatmap = (weekStartMs: number): WeekHeatmap => {
          const days: DayHeatmap[] = [];
          let weekActualHours = 0;
          for (let i = 0; i < 7; i++) {
            const dayMs = weekStartMs + i * 24 * 60 * 60 * 1000;
            const dayDate = new Date(dayMs);
            const dow = dayDate.getDay();
            const todaysCuts = cutsByDay.get(dayKey(dayMs)) ?? [];
            const hours: number[] = [];
            for (let h = HEATMAP_HOUR_START; h < HEATMAP_HOUR_END; h++) {
              const slotStart = dayMs + h * 60 * 60 * 1000;
              const slotEnd = slotStart + 60 * 60 * 1000;
              // Sum overlap (in ms) between every cut and this clock-
              // hour bucket — a 90-min cut from 1:30 → 3:00 contributes
              // 30 min to the 1pm slot, 60 min to 2pm, 0 to 3pm.
              let overlapMs = 0;
              for (const c of todaysCuts) {
                const oStart = Math.max(c.startMs, slotStart);
                const oEnd = Math.min(c.endMs, slotEnd);
                if (oEnd > oStart) overlapMs += oEnd - oStart;
              }
              const overlapMin = overlapMs / 60_000;
              weekActualHours += overlapMin / 60;
              // Utilization % of the barber's hourly target. 0 = no
              // activity (renders gray). Above 100 caps at "green" in
              // the component but the raw number flows through here so
              // downstream consumers can still tell the difference.
              const pct =
                overlapMin <= 0 ? 0 : Math.round((overlapMin / hourTargetMin) * 100);
              hours.push(pct);
            }
            days.push({
              day_ms: dayMs,
              dow,
              is_today: dayKey(dayMs) === todayKey,
              hours,
            });
          }
          // Gap = effective target hours - actual hours for THIS week.
          // Pulls the historical target via targetForWeek (honors mid-
          // year target changes) and multiplies by hourly_target_pct%
          // so the gap reads against the realism dial — same way the
          // progress bar on the main tile does. Null when no target
          // applies (very early users, weeks before any target was set).
          const weekTarget = targetForWeek(weekStartMs);
          const effectiveWeekTarget =
            weekTarget != null ? weekTarget * (hourlyTargetPct / 100) : null;
          const weekGapHours =
            effectiveWeekTarget != null && effectiveWeekTarget > 0
              ? Math.max(0, effectiveWeekTarget - weekActualHours)
              : 0;
          const missedCents =
            weekTarget != null &&
            weekTarget > 0 &&
            ytdCount >= OPPORTUNITY_MIN_YTD_VISITS &&
            ytdRevPerHourCents > 0
              ? Math.round(weekGapHours * ytdRevPerHourCents)
              : null;
          return {
            days,
            hour_start: HEATMAP_HOUR_START,
            hour_end: HEATMAP_HOUR_END,
            week_start_ms: weekStartMs,
            gap_hours: weekGapHours,
            missed_cents: missedCents,
          };
        };

        // Walk weekly_heatmaps oldest → newest. Each window is a
        // 7-day strip stepping back in 7-day chunks from the latest
        // window (which always ends TODAY). weeksSpan is the gap from
        // yearStart to the latest window in whole 7-day chunks.
        for (let w = weeksSpan; w >= 0; w--) {
          const weekStartMs = latestWindowStartMs - w * msPerWeek;
          weeklyHeatmaps.push(buildWeekHeatmap(weekStartMs));
        }
        heatmap = weeklyHeatmaps[weeklyHeatmaps.length - 1] ?? null;
        totalGapHours = heatmap?.gap_hours ?? 0;
      }

      // Opportunity (legacy field for the top-of-tile copy). Suppress
      // when YTD sample is too small to back the rate.
      let opportunityCents: number | null = null;
      if (heatmap && ytdCount >= OPPORTUNITY_MIN_YTD_VISITS && ytdRevPerHourCents > 0) {
        opportunityCents = totalGapHours * ytdRevPerHourCents;
      }

      const snapshot: CapacityUtilization = {
        target_hours_per_week: currentTarget,
        target_days_per_week: currentDays,
        daily_target_hours: dailyTargetHours,
        hourly_target_pct: hourlyTargetPct,
        this_week_target_hours: thisWeekTarget,
        this_week_actual_hours: thisWeek.hours,
        this_week_revenue_cents: thisWeek.revenue,
        this_week_visit_count: thisWeek.count,
        this_week_gap_hours: weekGapHours,
        this_week_missed_cents: weekMissedCents,
        ytd_actual_hours: ytdHours,
        ytd_revenue_cents: ytdRevenue,
        ytd_visit_count: ytdCount,
        ytd_weeks_elapsed: weeksElapsed,
        ytd_revenue_per_hour_cents: ytdRevPerHourCents,
        projected_annual_cents: projectedAnnualCents,
        potential_annual_cents: potentialAnnualCents,
        annual_gap_cents: annualGapCents,
        annual_target_hours: annualTargetHours,
        early_year_projection: earlyYearProjection,
        period_revenue_cents: periodRevenueCents,
        period_actual_hours: periodActualHours,
        period_revenue_rateable_cents: periodRevenueRateable,
        period_actual_hours_rateable: periodActualHoursRateable,
        period_dollar_per_hour_cents: periodDollarPerHourCents,
        period_target_hours: periodTargetHours,
        period_potential_cents: periodPotentialCents,
        period_gap_cents: periodGapCents,
        this_week_heatmap: heatmap,
        this_week_opportunity_cents: opportunityCents,
        weekly_heatmaps: weeklyHeatmaps,
      };
      return { data: snapshot, error: null };
    },
    [range, custom?.start ?? '', custom?.end ?? ''],
    ['visits', 'profile', 'capacity'],
  );
}

// Suppress unused warning — HEATMAP_HOURS is exported for tile components.
void HEATMAP_HOURS;

export type DayBucket = {
  date: string; // YYYY-MM-DD (local calendar day)
  ms: number; // ms-since-epoch for the day's start (used by popover lookup)
  /** Cut + add-on revenue. Tips live in `tip_cents` separately so the
   *  chart can overlay tips on top of each bar without inflating the
   *  bar that represents service revenue. */
  revenue_cents: number;
  tip_cents: number;
  visits: number;
  avg_ticket_cents: number;
};

// Returns one bucket per calendar day in the range, including zero-visit days,
// so the chart renders a continuous timeline (no gaps where a quiet day was).
export function useRevenueByDay(
  range: RangeKey = '30d',
  custom?: CustomRangeBounds | null,
) {
  return useFetch<DayBucket[]>(
    async () => {
      const start = rangeStartDate(range, custom);
      const end = rangeEndDate(range, custom);
      const { data: visits, error } = await supabase
        .from('visits')
        .select('started_at, price_cents, tip_cents')
        .gte('started_at', start.toISOString())
        .lte('started_at', end.toISOString())
        .order('started_at', { ascending: true });
      if (error) return { data: null, error };
      const list = visits ?? [];

      // Build the full day spine first (zero-fill), then layer visits on top.
      const buckets: DayBucket[] = [];
      const cursor = new Date(start);
      while (cursor.getTime() <= end.getTime()) {
        const dayStart = new Date(cursor);
        dayStart.setHours(0, 0, 0, 0);
        buckets.push({
          date: toIsoDate(dayStart),
          ms: dayStart.getTime(),
          revenue_cents: 0,
          tip_cents: 0,
          visits: 0,
          avg_ticket_cents: 0,
        });
        cursor.setDate(cursor.getDate() + 1);
      }

      // Index by date string for O(1) bucket lookup
      const byDate = new Map<string, DayBucket>();
      for (const b of buckets) byDate.set(b.date, b);
      for (const v of list) {
        const d = new Date(v.started_at);
        d.setHours(0, 0, 0, 0);
        const key = toIsoDate(d);
        const bucket = byDate.get(key);
        if (!bucket) continue; // shouldn't happen given the gte/lte bounds
        bucket.revenue_cents += v.price_cents ?? 0;
        bucket.tip_cents += v.tip_cents ?? 0;
        bucket.visits += 1;
      }
      for (const b of buckets) {
        b.avg_ticket_cents = b.visits > 0 ? Math.round(b.revenue_cents / b.visits) : 0;
      }
      return { data: buckets, error: null };
    },
    [range, custom?.start ?? '', custom?.end ?? ''],
    ['visits']
  );
}

function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function useTopClients(limit: number = 5) {
  return useFetch<Array<ClientStatsView & { client: ClientRow }>>(
    async () => {
      // Eligibility: 3-visit minimum (badge floor) + visit logged within
      // the last 90 days (active client). Without the 90-day filter, a
      // client who moved away or stopped coming would sit at the top of
      // the list indefinitely; with it, they drop off and get a chance
      // to surface as at-risk so the barber can reach out before the
      // relationship is permanently lost.
      const ninetyDaysAgoIso = new Date(Date.now() - 90 * 86_400_000).toISOString();
      const { data: stats, error: statsError } = await supabase
        .from('client_stats')
        .select('*')
        .gte('visit_count', 3)
        .gte('last_visit_at', ninetyDaysAgoIso)
        .order('total_revenue_cents', { ascending: false })
        .limit(limit);
      if (statsError) return { data: null, error: statsError };
      if (!stats || stats.length === 0) return { data: [], error: null };

      const clientIds = stats.map((s) => s.client_id);
      const { data: clients, error: clientsError } = await supabase
        .from('clients')
        .select('*')
        .in('id', clientIds);
      if (clientsError) return { data: null, error: clientsError };
      if (!clients) return { data: [], error: null };

      const clientById = new Map(clients.map((c) => [c.id, c]));
      const data: Array<ClientStatsView & { client: ClientRow }> = [];
      for (const s of stats) {
        const client = clientById.get(s.client_id);
        if (client) data.push({ ...s, client });
      }
      return { data, error: null };
    },
    [limit],
    ['stats', 'clients', 'visits']
  );
}
